// ============================================================
// Webpack Imports
// ============================================================
import * as bootstrap from 'bootstrap';
import WaveSurfer from 'wavesurfer.js';
import fitty from 'fitty';
import { Editor } from '@tiptap/core';
import { Placeholder } from '@tiptap/extensions';
import StarterKit from '@tiptap/starter-kit';


// ============================================================
// § 1  通知系统 (Notifications)
// ============================================================

/**
 * 显示 Bootstrap Toast 通知。
 * @param {Object} options
 * @param {string} options.content    通知内容（支持 HTML）
 * @param {string} options.type       类型：'info' | 'warning' | 'danger' | 'success'
 * @param {number} options.delay      显示时长（毫秒，0 = 不自动关闭）
 * @param {string} options.position   Bootstrap 定位类，例如 'top-0 end-0'
 * @param {boolean} options.dismissible 是否显示关闭按钮
 *
 * 示例：
 *   km_notification({ content: '操作成功！', type: 'success', delay: 3000,
 *                     position: 'top-0 end-0', dismissible: true });
 */
window.km_notification = function (options = {}) {
	const defaults = {
		content: '',
		type: 'info',
		delay: 5000,
		position: 'top-0 end-0',
		dismissible: true,
	};
	const config = { ...defaults, ...options };
	if (!config.content) return;

	// 按定位查找或创建 Toast 容器
	const containerSelector = `.toast-container.${config.position.split(' ').join('.')}`;
	let container = document.querySelector(containerSelector);
	if (!container) {
		container = document.createElement('div');
		container.className = `toast-container position-fixed p-3 ${config.position}`;
		container.style.zIndex = '1080';
		document.body.appendChild(container);
	}

	// 构建 Toast HTML
	const bgClass      = `text-bg-${config.type}`;
	const isDarkText   = ['warning', 'info'].includes(config.type);
	const closeBtnClass = `btn-close ${isDarkText ? '' : 'btn-close-white'} me-2 m-auto`;
	const closeBtnHtml  = config.dismissible
		? `<button type="button" class="${closeBtnClass}" data-bs-dismiss="toast" aria-label="Close"></button>`
		: '';

	const toastId   = `km-toast-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
	const toastHtml = `
		<div id="${toastId}" class="toast align-items-center ${bgClass} border-0"
		     role="alert" aria-live="assertive" aria-atomic="true">
			<div class="d-flex">
				<div class="toast-body">${config.content}</div>
				${closeBtnHtml}
			</div>
		</div>`;

	const template = document.createElement('template');
	template.innerHTML = toastHtml.trim();
	const toastElement = template.content.firstChild;
	container.appendChild(toastElement);

	const bsToast = new bootstrap.Toast(toastElement, {
		autohide: config.delay > 0,
		delay: config.delay || 5000,
	});
	bsToast.show();

	// Toast 隐藏后从 DOM 移除，容器为空时也一并移除
	toastElement.addEventListener('hidden.bs.toast', () => {
		toastElement.remove();
		if (container.children.length === 0) container.remove();
	});
};


// ============================================================
// § 2  WaveSurfer 音频播放器
// ============================================================

const _wavePlayers = new Set();
const _waveObserver = new IntersectionObserver((entries) => {
	entries.forEach(entry => {
		const wrapper = entry.target;
		if (!entry.isIntersecting && wrapper._wavesurfer && wrapper._wavesurfer.isPlaying()) {
			wrapper._wavesurfer.pause();
		}
	});
}, { threshold: 0 });

function formatTime(seconds) {
	if (!seconds || isNaN(seconds)) return '0:00';
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60).toString().padStart(2, '0');
	return `${m}:${s}`;
}

function createWaveSurferPlayer(wrapper) {
	if (wrapper.dataset.wavesurferInited) return;

	const src        = wrapper.dataset.audioSrc;
	const waveformEl = wrapper.querySelector('.waveform');
	const playBtn    = wrapper.querySelector('.wave-play');
	const currentEl  = wrapper.querySelector('.wave-current');
	const durationEl = wrapper.querySelector('.wave-duration');

	if (!src || !waveformEl) return;

	const isCompact = wrapper.classList.contains('audio-player--compact');

	const ws = WaveSurfer.create({
		container:     waveformEl,
		waveColor:     getComputedStyle(document.documentElement).getPropertyValue('--km-accent')    || '#c9602a',
		progressColor: getComputedStyle(document.documentElement).getPropertyValue('--km-ink-muted') || '#A8A29E',
		cursorColor:   getComputedStyle(document.documentElement).getPropertyValue('--km-border')    || '#E7E3DD',
		cursorWidth:   1,
		barWidth:      isCompact ? 3 : 4,
		barHeight:     isCompact ? 0.7 : 0.85,
		barGap:        isCompact ? 4 : 4,
		barRadius:     isCompact ? 3 : 4,
		normalize:     true,
		fillParent:    true,
		mediaControls: false,
		interact:      true,
		dragToSeek:    true,
		hideScrollbar: true,
		sampleRate:    14000,
		responsive:    true,
		height:        isCompact ? 28 : 56,
	});

	wrapper._wavesurfer = ws;
	wrapper.dataset.wavesurferInited = '1';
	_wavePlayers.add(wrapper);
	_waveObserver.observe(wrapper);

	ws.load(src);

	ws.on('ready',        () => { if (durationEl) durationEl.textContent = '/ ' + formatTime(ws.getDuration()); });
	ws.on('audioprocess', () => { if (currentEl)  currentEl.textContent  = formatTime(ws.getCurrentTime()); });
	ws.on('seek',         () => { if (currentEl)  currentEl.textContent  = formatTime(ws.getCurrentTime()); });

	ws.on('play', () => {
		_wavePlayers.forEach(other => {
			if (other !== wrapper && other._wavesurfer && other._wavesurfer.isPlaying()) {
				other._wavesurfer.pause();
			}
		});
		if (!playBtn) return;
		playBtn.classList.add('is-playing');
		playBtn.querySelector('i')?.classList.replace('fa-play', 'fa-pause');
	});
	ws.on('pause', () => {
		if (!playBtn) return;
		playBtn.classList.remove('is-playing');
		playBtn.querySelector('i')?.classList.replace('fa-pause', 'fa-play');
	});

	if (playBtn) {
		playBtn.addEventListener('click', (e) => {
			e.preventDefault();
			ws.isPlaying() ? ws.pause() : ws.play();
		});
	}
}

function initWaveSurfer() {
	// Process existing .audio-player elements (primary audio from audio.php)
	document.querySelectorAll('.audio-player').forEach(createWaveSurferPlayer);

	// Transform native <audio> elements into compact WaveSurfer players (extra audio)
	document.querySelectorAll('#km-feed audio, .km-feed audio, .entry-content audio').forEach((audioEl) => {
		if (audioEl.closest('.audio-player') || audioEl.dataset.wavesurferDone) return;

		const src = audioEl.querySelector('source')?.src || audioEl.src;
		if (!src) return;

		const wrapper = document.createElement('div');
		wrapper.className = 'audio-player audio-player--compact';
		wrapper.dataset.audioSrc = src;
		const uid = 'waveform-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
		wrapper.innerHTML = `
			<button class="wave-play" aria-label="Play/Pause"><i class="fas fa-play"></i></button>
			<div class="waveform" id="${uid}" aria-hidden="true"></div>
			<span class="wave-current">0:00</span>`;

		audioEl.parentNode.insertBefore(wrapper, audioEl);
		audioEl.style.display = 'none';
		audioEl.dataset.wavesurferDone = '1';

		createWaveSurferPlayer(wrapper);
	});
}

window.initWaveSurfer = initWaveSurfer;


// ============================================================
// § 3  主题切换（Theme Toggle）
// ============================================================

function initThemeToggle() {
	const themeBtn  = document.getElementById('themeBtn');
	const themeIcon = document.getElementById('themeIcon');
	if (!themeBtn) return;

	const THEMES = ['system', 'light', 'dark'];

	function setThemeIcon(mode) {
		if (!themeIcon) return;
		const isIconTag = themeIcon.tagName?.toLowerCase() === 'i';
		const iconMap   = {
			system: isIconTag ? 'fa-solid fa-circle-half-stroke' : 'S',
			light:  isIconTag ? 'fa-solid fa-sun'                : 'L',
			dark:   isIconTag ? 'fa-solid fa-moon'               : 'D',
		};
		if (isIconTag) {
			themeIcon.className = iconMap[mode];
		} else {
			themeIcon.textContent = iconMap[mode];
		}
	}

	function applyTheme(mode) {
		const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
		const resolved    = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;
		document.documentElement.setAttribute('data-bs-theme', resolved);
	}

	function syncUI(mode) {
		applyTheme(mode);
		setThemeIcon(mode);
	}

	let currentMode = THEMES.includes(localStorage.getItem('km-theme'))
		? localStorage.getItem('km-theme')
		: 'system';

	syncUI(currentMode);

	window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
		if (currentMode === 'system') applyTheme('system');
	});

	themeBtn.addEventListener('click', () => {
		currentMode = THEMES[(THEMES.indexOf(currentMode) + 1) % THEMES.length];
		localStorage.setItem('km-theme', currentMode);
		syncUI(currentMode);
	});
}


// ============================================================
// § 4  API 与动态 Feed（Ajax Core & Feed）
// ============================================================

// ── 4.0 独立 API 工具 ──────────────────────────────────────

async function api(path, options = {}) {
	const s = window.keepMinutesAjax || {};
	const headers = Object.assign({ 'X-WP-Nonce': s.nonce }, options.headers || {});
	const response = await fetch(
		s.root + path.replace(/^\//, ''),
		Object.assign({}, options, { headers })
	);
	const data = await response.json().catch(() => ({}));
	if (!response.ok) throw new Error(data.message || s.i18n?.error);
	return data;
}

// ── 4.0.1 上传体积校验 ────────────────────────────────────

// Set when the latest pick was rejected for exceeding the PHP limit. It lets
// submit explain the real cause instead of the misleading "add some content".
let kmOversizedRejected = false;

function kmMaxUploadBytes() {
	const value = parseInt(window.keepMinutesAjax?.maxUploadSize, 10);
	return Number.isFinite(value) && value > 0 ? value : 0;
}

function kmPostMaxBytes() {
	const value = parseInt(window.keepMinutesAjax?.postMaxSize, 10);
	return Number.isFinite(value) && value > 0 ? value : 0;
}

function kmFormatMb(bytes) {
	const mb = (bytes || 0) / 1048576;
	return mb.toFixed(1).replace(/\.0$/, '');
}

function kmMaxUploadMbLabel() {
	const max = kmMaxUploadBytes();
	return max > 0 ? kmFormatMb(max) : '0';
}

function kmFileTooLargeMessage() {
	const template = window.keepMinutesAjax?.i18n?.fileTooLarge
		|| 'File is too large. The maximum allowed size is %s MB.';
	return template.replace('%s', kmMaxUploadMbLabel());
}

/**
 * Split a file input's selection into accepted and oversized files.
 * Shows one toast when anything is rejected.
 *
 * @param {HTMLInputElement} input File input element.
 * @returns {{accepted: File[], oversized: File[]}}
 */
function kmFilterOversizedFiles(input) {
	const max  = kmMaxUploadBytes();
	const list = Array.from(input?.files || []);
	if (!max) return { accepted: list, oversized: [] };

	const accepted  = list.filter((file) => file.size <= max);
	const oversized = list.filter((file) => file.size > max);

	// Track a pick that produced nothing usable: the composer looks empty
	// afterwards, so submit must explain "file too large" rather than
	// "add some content". Any usable pick clears the flag.
	if (oversized.length) {
		kmOversizedRejected = accepted.length === 0;
		window.km_notification({
			content: kmFileTooLargeMessage(),
			type: 'danger',
			delay: 6000,
		});
	} else if (accepted.length) {
		kmOversizedRejected = false;
	}

	return { accepted, oversized };
}

/**
 * Sum the total size of the files about to be submitted and compare it
 * against PHP's post_max_size, which would otherwise drop the whole body.
 *
 * @param {File[]} files Candidate files.
 * @returns {boolean} True when the total exceeds the limit.
 */
function kmFilesExceedPostMax(files) {
	const max  = kmPostMaxBytes();
	const list = Array.from(files || []);
	if (!max || !list.length) return false;

	const total = list.reduce((sum, file) => sum + (file?.size || 0), 0);
	return total > max;
}

function htmlToElement(html) {
	const template = document.createElement('template');
	template.innerHTML = html.trim();
	return template.content.firstElementChild;
}

function skeletonCardHTML() {
	const tpl = document.getElementById('skeleton-default');
	return tpl ? tpl.innerHTML.trim() : '';
}

function skeletonImageCardHTML() {
	const tpl = document.getElementById('skeleton-image');
	return tpl ? tpl.innerHTML.trim() : '';
}

function skeletonGeolocationCompactCardHTML() {
	const tpl = document.getElementById('skeleton-geo-compact');
	return tpl ? tpl.innerHTML.trim() : '';
}

function getStartPage() {
	const urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
	if (urlPage > 1) return urlPage;
	const feed = document.getElementById('km-feed');
	if (feed && feed.dataset.currentPage) {
		return parseInt(feed.dataset.currentPage, 10) || 1;
	}
	return 1;
}

function initInfiniteScroll(config) {
	const {
		feed,
		seenPosts,
		perPage,
		getStartPage,
		initialHasMore = true,
		endpoint = 'posts',
		extraParams = {},
		skeletonHTML,
		onBatchComplete,
	} = config;

	if (!feed || perPage <= 0) return;

	const ROOT_MARGIN = 300; // 与 observer 的 rootMargin 保持一致
	let currentPage = getStartPage();
	let loading = false;
	let hasMore = initialHasMore;
	let destroyed = false;

	const sentinel = document.createElement('div');
	sentinel.className = 'km-scroll-sentinel';
	sentinel.setAttribute('aria-hidden', 'true');
	feed.after(sentinel);

	// 哨兵是否已进入预加载范围（即 rootMargin 之内）。
	function sentinelInRange() {
		return sentinel.getBoundingClientRect().top <= window.innerHeight + ROOT_MARGIN;
	}

	async function loadNext() {
		if (destroyed || loading || !hasMore) return;

		loading = true;
		currentPage++;
		let failed = false;

		const frag = document.createDocumentFragment();
		for (let i = 0; i < perPage; i++) {
			frag.appendChild(htmlToElement(skeletonHTML()));
		}
		feed.appendChild(frag);

		const params = new URLSearchParams(extraParams);
		params.set('per_page', String(perPage));
		params.set('page', String(currentPage));

		try {
			const data = await api(endpoint + '?' + params.toString());
			const posts = data.posts || [];
			const loadedPage = data.page || currentPage;

			const skeletons = feed.querySelectorAll('[data-feed-skeleton]');
			const sArr = Array.from(skeletons);

			posts.forEach((post, index) => {
				if (!post || !post.html) return;
				if (post.id && seenPosts.has(post.id)) return;

				if (post.id) seenPosts.add(post.id);
				const card = htmlToElement(post.html);
				if (!card) return; // 渲染为空时跳过，避免单条坏数据打断整批

				card.dataset.infinitePage = loadedPage;
				card.classList.add('km-card-enter');

				const ph = sArr[index];
				if (ph) {
					ph.replaceWith(card);
				} else {
					feed.appendChild(card);
				}
				requestAnimationFrame(() => card.classList.add('is-visible'));
			});

			if (posts.length < sArr.length) {
				sArr.slice(posts.length).forEach(el => el.remove());
			}

			hasMore = data.has_more !== false && posts.length >= perPage;

			if (onBatchComplete) onBatchComplete();
		} catch (error) {
			failed = true;
			feed.querySelectorAll('[data-feed-skeleton]').forEach(el => el.remove());
			console.error('Infinite scroll error:', error);
		} finally {
			loading = false;
			// 加载期间用户若已把哨兵滚回预加载范围，IntersectionObserver 的进入事件
			// 已被 loading 吞掉且不会再派发，这里补一次触发，避免无限加载永久停住。
			if (!destroyed && !failed && hasMore && sentinelInRange()) {
				window.setTimeout(loadNext, 0);
			}
		}
	}

	const observer = new IntersectionObserver((entries) => {
		if (entries[0].isIntersecting) loadNext();
	}, { rootMargin: ROOT_MARGIN + 'px', threshold: 0 });

	observer.observe(sentinel);

	let scrollTicking = false;
	function syncPageUrlFromScroll() {
		if (scrollTicking) return;
		scrollTicking = true;
		requestAnimationFrame(() => {
			scrollTicking = false;
			const cards = feed.querySelectorAll('.article-warper[data-infinite-page]');
			let topPage = 0;
			let minTop = Infinity;
			cards.forEach((card) => {
				const rect = card.getBoundingClientRect();
				if (rect.bottom > 0 && rect.top >= 0 && rect.top < minTop) {
					minTop = rect.top;
					topPage = parseInt(card.dataset.infinitePage, 10) || 0;
				}
			});
			const currentUrlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 0;
			if (topPage !== currentUrlPage) {
				if (topPage > 1) {
					const url = new URL(window.location);
					url.searchParams.set('page', String(topPage));
					history.replaceState({ page: topPage }, '', url);
				} else if (currentUrlPage > 0) {
					const url = new URL(window.location);
					url.searchParams.delete('page');
					history.replaceState({ page: 1 }, '', url);
				}
			}
		});
	}

	window.addEventListener('scroll', syncPageUrlFromScroll, { passive: true });

	syncPageUrlFromScroll();

	return function destroy() {
		destroyed = true;
		observer.disconnect();
		sentinel.remove();
		window.removeEventListener('scroll', syncPageUrlFromScroll);
	};
}

function initFeed() {
	const settings = window.keepMinutesAjax;
	if (!settings || !settings.root) return;

	const feed      = document.getElementById('km-feed');
	const form      = document.getElementById('km-compose-form');
	const seenPosts = new Set();
	const feedPageSize = Math.max(1, parseInt(settings.postsPerPage || 10, 10) || 10);
	let feedPollingTimer = null;
	let initialFeedLoaded = false;
	let _extraAudioFiles = [];
	let _extraImageFiles = [];
	window._primaryImageFiles = [];
	window._kmRecorder = {
		recorder: null,
		stream: null,
		chunks: [],
		startTime: 0,
		timer: null,
		blob: null
	};
	window._kmChatCharacters = window._kmChatCharacters || [];

	function buildFeedQuery(after = '', page = 0) {
		const params = new URLSearchParams();
		params.set('per_page', String(feedPageSize));
		if (after) {
			params.set('after', after);
		}
		if (page > 0) {
			params.set('page', String(page));
		}
		return params.toString();
	}

	// ── 4.2 Feed 卡片操作 ─────────────────────────────────────

	function rememberCards() {
		if (!feed) return;
		feed.querySelectorAll('.article-warper[data-post-id]').forEach((card) => {
			seenPosts.add(card.dataset.postId);
		});
	}

	function updateLatest(timestamp) {
		if (!feed || !timestamp) return;
		const current = parseInt(feed.dataset.latestTimestamp, 10) || 0;
		const incoming = parseInt(timestamp, 10);
		if (!current || incoming > current) {
			feed.dataset.latestTimestamp = String(timestamp);
		}
	}

	function clearFeedShell() {
		if (!feed) return;
		feed.querySelectorAll('[data-feed-skeleton], [data-km-empty-feed]').forEach((node) => node.remove());
	}

	function showEmptyFeedState() {
		if (!feed) return;
		feed.innerHTML = `
			<div class="km-card card mb-0" role="status" data-km-empty-feed>
				${settings.i18n.noPosts}
			</div>`;
		feed.dataset.latestTimestamp = '';
		feed.setAttribute('aria-busy', 'false');
	}

	function renderInitialFeed(posts, latestTimestamp, page) {
		if (!feed) return;

		const skeletons = Array.from(feed.querySelectorAll('[data-feed-skeleton]'));
		const cards = Array.isArray(posts) ? posts : [];

		if (!cards.length) {
			showEmptyFeedState();
			return;
		}

		let renderedCount = 0;
		cards.forEach((post, index) => {
			if (!post || !post.html) return;

			const card = htmlToElement(post.html);
			if (!card) return;

			seenPosts.add(card.dataset.postId);
			if (page) card.dataset.infinitePage = page;
			card.classList.add('km-card-enter');

			const placeholder = skeletons[index];
			if (placeholder) {
				placeholder.replaceWith(card);
			} else {
				feed.appendChild(card);
			}

			requestAnimationFrame(() => card.classList.add('is-visible'));
			renderedCount++;
		});

		if (renderedCount < skeletons.length) {
			skeletons.slice(renderedCount).forEach((node) => node.remove());
		}

		updateLatest(latestTimestamp || cards[0]?.timestamp || '');
		feed.setAttribute('aria-busy', 'false');
		if (window.initWaveSurfer) window.initWaveSurfer();
		initShareButtons();
		initExpandableImages();
		initExpandableAside();
		initFitty();
	}

	function insertCard(html, timestamp) {
		if (!feed || !html) return null;

		clearFeedShell();

		const card = htmlToElement(html);
		if (!card || seenPosts.has(card.dataset.postId)) return null;

		seenPosts.add(card.dataset.postId);
		card.classList.add('km-card-enter');
		feed.prepend(card);
		updateLatest(timestamp || card.dataset.timestamp);
		requestAnimationFrame(() => card.classList.add('is-visible'));

		if (window.initWaveSurfer) window.initWaveSurfer();
		initShareButtons();
		initExpandableImages();
		initExpandableAside();
		initFitty();
		return card;
	}

	function replaceCard(card, html, timestamp) {
		const next = htmlToElement(html);
		if (!next) return;
		seenPosts.add(next.dataset.postId);
		card.replaceWith(next);
		updateLatest(timestamp || next.dataset.timestamp);
		if (window.initWaveSurfer) window.initWaveSurfer();
		initShareButtons();
		initExpandableImages();
		initExpandableAside();
		initFitty();
	}

	// ── 4.3 UI 工具函数 ───────────────────────────────────────

	//点击发布按钮后，显示加载状态，禁用按钮，防止重复提交
	function setBusy(button, busy, label) {
		if (!button) return;
		if (busy) {
			button.dataset.originalHtml = button.innerHTML;
			button.disabled = true;
			button.innerHTML = `<i class="fa-solid fa-spinner fa-spin"></i><span>${label}</span>`;
		} else {
			button.disabled = false;
			button.innerHTML = button.dataset.originalHtml || settings.i18n.publish;
		}
	}

	function getTextFromHtml(html) {
		const holder = document.createElement('div');
		holder.innerHTML = html || '';
		return holder.textContent.replace(/ /g, ' ').trim();
	}

	// ── 4.5 Composer 内容校验与重置 ──────────────────────────

	function formHasPostContent(composeForm) {
		const textFieldNames = [
			'photo_caption', 'audio_title', 'audio_desc',
			'quote_content', 'quote_source', 'quote_comment',
			'video_url', 'video_caption', 'audio_url',
			'aside_content', 'chat_content',
		];
		const hasText     = textFieldNames.some((name) => {
			const field = composeForm.elements[name];
			return field && field.value.trim();
		});
		// 使用 getText() 判断编辑器是否有内容（isEmpty 可能不准确）
		const hasRichText = !!document.getElementById('rte-content') && window.kmEditor?.getText().trim() !== '';
		const hasFiles    = Array.from(composeForm.querySelectorAll('input[type="file"]'))
			.some((input) => input.files && input.files.length > 0);
		const asideFormat = document.getElementById('post_format_field')?.value === 'aside';
		const hasAsideIcon = asideFormat && document.getElementById('aside')?.value && document.getElementById('aside')?.value !== 'blankface' && document.getElementById('aside')?.name === 'cstatus';
		return hasText || hasRichText || hasFiles || _extraAudioFiles.length > 0 || _extraImageFiles.length > 0 || window._primaryImageFiles.length > 0 || hasAsideIcon;
	}

	function resetComposer(composeForm) {
		composeForm.reset();
		['primary-img-preview', 'primary-audio-preview', 'extra-attachments', 'tag-display-row', 'location-display']
			.forEach((id) => {
				const el = document.getElementById(id);
				if (!el) return;
				el.innerHTML = '';
				if ('hidden' in el) el.hidden = true;
			});

		_extraAudioFiles = [];
		_extraImageFiles = [];
		window._primaryImageFiles = [];
		kmOversizedRejected = false;
		const rec = window._kmRecorder;
		if (rec.recorder && rec.recorder.state === 'recording') rec.recorder.stop();
		rec.recorder = null;
		if (rec.stream) { rec.stream.getTracks().forEach(t => t.stop()); rec.stream = null; }
		rec.chunks = [];
		rec.blob = null;
		clearInterval(rec.timer);
		rec.timer = null;
		const previewEl = document.getElementById('recorder-preview');
		if (previewEl) { if (previewEl.src) URL.revokeObjectURL(previewEl.src); previewEl.src = ''; }
		showRecorderState('idle');
		window._kmChatCharacters  = [];

		const hiddenContent = document.getElementById('post_content_field');
		const tags  = document.getElementById('tags_hidden');
		const count = document.getElementById('charCount');
		if (window.kmEditor)  window.kmEditor.commands.clearContent();
		if (hiddenContent)    hiddenContent.value = '';
		if (tags)             tags.value          = '';

		const chatContent = document.getElementById('chat-content');
		const chatLineInput = document.getElementById('chat-line-input');
		const chatCharRadioGroup = document.getElementById('chat-char-radio-group');
		const chatCharHidden = document.getElementById('chat-characters-hidden');
		if (chatContent) chatContent.value = '';
		if (chatLineInput) chatLineInput.value = '';
		if (chatCharRadioGroup) chatCharRadioGroup.innerHTML = '';
		if (chatCharHidden) chatCharHidden.value = '';

		if (window.keepMinutesUpdateCharCount) window.keepMinutesUpdateCharCount();
	}

	// ── 4.6 表单提交 ─────────────────────────────────────────

	/**
	 * Collect every file that will be part of the request body.
	 *
	 * @param {HTMLFormElement} composeForm Composer form.
	 * @returns {File[]}
	 */
	function collectPendingFiles(composeForm) {
		const files = [];
		const seen  = new Set();

		const push = (file) => {
			if (!file || seen.has(file)) return;
			seen.add(file);
			files.push(file);
		};

		composeForm.querySelectorAll('input[type="file"]').forEach((input) => {
			Array.from(input.files || []).forEach(push);
		});
		window._primaryImageFiles.forEach(push);
		_extraImageFiles.forEach(push);
		_extraAudioFiles.forEach(push);
		if (window._kmRecorder?.blob) push(window._kmRecorder.blob);

		return files;
	}

	function handleAjaxSubmit(event) {
		event.preventDefault();
		if (!form) return;

		const hiddenContent = document.getElementById('post_content_field');
		if (window.kmEditor && hiddenContent) {
			hiddenContent.value = window.kmEditor.getHTML();
		}

		// An oversized pick is dropped from the inputs, so the composer may look
		// empty. Explain the real cause instead of "add some content".
		if (!formHasPostContent(form)) {
			if (kmOversizedRejected) {
				km_notification({ content: kmFileTooLargeMessage(), type: 'danger', delay: 6000 });
			} else {
				km_notification({ content: settings.i18n.emptyContent, type: 'warning', delay: 5000 });
			}
			return;
		}

		// There is other content to publish, so the earlier rejection no longer blocks.
		kmOversizedRejected = false;

		const pendingFiles = collectPendingFiles(form);
		const maxUpload    = kmMaxUploadBytes();
		const oversized    = maxUpload > 0
			? pendingFiles.filter((file) => file.size > maxUpload)
			: [];

		if (oversized.length) {
			km_notification({ content: kmFileTooLargeMessage(), type: 'danger', delay: 6000 });
			return;
		}

		if (kmFilesExceedPostMax(pendingFiles)) {
			km_notification({ content: settings.i18n.totalTooLarge, type: 'danger', delay: 6000 });
			return;
		}

		const submitBtn = form.querySelector('.btn-post');
		setBusy(submitBtn, true, settings.i18n.publishing);

		const formData = new FormData(form);
		if (window._kmRecorder.blob) {
			formData.set('audio_primary', window._kmRecorder.blob, window._kmRecorder.blob.name);
		}
		_extraAudioFiles.forEach(f => formData.append('extra_audio[]', f));
		_extraImageFiles.forEach(f => formData.append('extra_images[]', f));
		// 用 _primaryImageFiles 数组替换表单中的 photo_primary[]，支持多次选图累积
		formData.delete('photo_primary[]');
		window._primaryImageFiles.forEach(f => formData.append('photo_primary[]', f));

		api('posts', { method: 'POST', body: formData })
			.then((data)  => { insertCard(data.html, data.timestamp); resetComposer(form); })
			.catch((error) => { km_notification({ content: error.message, type: 'danger' }); console.error('Error submitting post:', error); })
			.finally(() => setBusy(submitBtn, false));
	}

	window.keepMinutesHandleAjaxSubmit = handleAjaxSubmit;

	if (form) form.addEventListener('submit', handleAjaxSubmit);

	document.getElementById('extraImg')?.addEventListener('change', function () { handleExtraImages(this); });
	document.getElementById('extraAudio')?.addEventListener('change', function () { handleExtraAudio(this); });

	function loadInitialFeed(extraParams = {}) {
		if (!feed) return Promise.resolve(true);

		const startPage = getStartPage();
		const query = buildFeedQuery('', startPage);
		const params = new URLSearchParams(query);
		Object.entries(extraParams).forEach(([k, v]) => params.set(k, v));
		return api('posts?' + params.toString())
			.then((data) => {
				renderInitialFeed(data.posts || [], data.latest_timestamp, startPage);
				if (startPage > 1) {
					feed.dataset.latestTimestamp = '';
				}
				return data.has_more !== false;
			})
			.catch((error) => {
				console.error('Error loading feed:', error);
				km_notification({
					content: error.message || settings.i18n.error,
					type: 'danger',
					delay: 5000,
				});
				return false;
			})
			.finally(() => {
				initialFeedLoaded = true;
				if (feed) {
					feed.setAttribute('aria-busy', 'false');
				}
			});
	}

	// ── 4.7 轮询新帖（前端节流 + 静态版本信号） ──────────────

	const POLL_MAX_DELAY = 300000; // 退避上限 5 分钟
	let feedPollingActive = false;
	let pollInFlight = false;
	let pollIdleStreak = 0;

	function basePollDelay() {
		const seconds = Math.max(5, parseInt(settings.pollSeconds, 10) || 30);
		return seconds * 1000;
	}

	// 静态版本文件优先：未变化时完全跳过 PHP 请求。失败则回退到 API。
	async function hasRemoteUpdates() {
		const url = settings.feedVersionUrl;
		if (!url || !feed) return true;

		try {
			const res = await fetch(url, { cache: 'no-store' });
			if (!res.ok) return true;
			const data = await res.json();
			const version = parseInt(data.version, 10) || 0;
			const latest = parseInt(feed.dataset.latestTimestamp, 10) || 0;
			return version > latest;
		} catch (error) {
			return true; // 回退到 API 轮询，由后端版本短路兜底
		}
	}

	function scheduleNextPoll() {
		if (!feedPollingActive) return;
		if (feedPollingTimer) {
			window.clearTimeout(feedPollingTimer);
			feedPollingTimer = null;
		}
		if (document.hidden) return; // 后台标签页暂停轮询

		const delay = Math.min(basePollDelay() * Math.pow(2, pollIdleStreak), POLL_MAX_DELAY);
		const jittered = Math.round(delay * (0.85 + Math.random() * 0.3));
		feedPollingTimer = window.setTimeout(pollPosts, jittered);
	}

	async function pollPosts() {
		if (!feed || !initialFeedLoaded || pollInFlight) return;
		pollInFlight = true;

		try {
			if (!(await hasRemoteUpdates())) {
				// 全局无新内容：不发 PHP 请求，直接退避。
				pollIdleStreak = Math.min(pollIdleStreak + 1, 6);
				return;
			}

			const after = feed.dataset.latestTimestamp || '';
			const query = buildFeedQuery(after);
			const data = await api('posts?' + query);
			const posts = data.posts || [];
			let newCount = 0;
			posts.slice().reverse().forEach((post) => {
				if (insertCard(post.html, post.timestamp)) newCount++;
			});
			updateLatest(data.latest_timestamp);

			if (newCount > 0) {
				pollIdleStreak = 0;
				window.km_notification({
					content:    settings.i18n.newPost,
					type:       'success',
					delay:      8000,
					position:   'top-0 end-0',
					dismissible: true,
				});
			} else {
				pollIdleStreak = Math.min(pollIdleStreak + 1, 6);
			}
		} catch (error) {
			// 网络错误静默忽略，等待下一次调度。
		} finally {
			pollInFlight = false;
			scheduleNextPoll();
		}
	}

	function startFeedPolling() {
		if (!feed || feedPollingActive) return;
		feedPollingActive = true;
		pollIdleStreak = 0;
		pollPosts();
	}

	function stopFeedPolling() {
		feedPollingActive = false;
		if (feedPollingTimer) {
			window.clearTimeout(feedPollingTimer);
			feedPollingTimer = null;
		}
	}

	window.kmStopFeedPolling = stopFeedPolling;

	// 后台标签页暂停轮询，回到前台立即补一次。
	document.addEventListener('visibilitychange', () => {
		if (!feedPollingActive) return;
		if (document.hidden) {
			if (feedPollingTimer) {
				window.clearTimeout(feedPollingTimer);
				feedPollingTimer = null;
			}
		} else {
			pollPosts();
		}
	});

	

	// ── 4.8 卡片交互（点赞、评论、编辑、删除） ──────────────

	function closestCard(target) {
		return target.closest('.article-warper[data-post-id]');
	}

	function updateReactionUI(card, data) {
		let summary = card.querySelector('.km-reactions-summary');
		const kmCard = card.querySelector('.km-card');

		if (data.html) {
			if (summary) {
				summary.outerHTML = data.html;
			} else if (kmCard) {
				const footer = kmCard.querySelector('.card-footer');
				const wrapper = document.createElement('div');
				wrapper.innerHTML = data.html.trim();
				const newSummary = wrapper.firstElementChild;
				if (newSummary && footer) {
					kmCard.insertBefore(newSummary, footer);
				}
			}
		} else if (summary) {
			summary.remove();
		}

		const active = Array.isArray(data.active) ? data.active.map(Number) : [];
		card.querySelectorAll('.km-reaction-emoji-btn').forEach((btn) => {
			const emojiId = parseInt(btn.dataset.emoji, 10);
			btn.classList.toggle('is-active', active.includes(emojiId));
		});
	}

	function closeReactionMenus(exceptCard) {
		document.querySelectorAll('.km-reaction-menu:not([hidden])').forEach((menu) => {
			const card = menu.closest('.article-warper[data-post-id]');
			if (!exceptCard || card !== exceptCard) {
				menu.hidden = true;
				const trigger = card && card.querySelector('.km-reaction-trigger');
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
			}
		});
	}

	function toggleReactionMenu(card, trigger) {
		const menu = card.querySelector('.km-reaction-menu');
		if (!menu) return;
		const willOpen = menu.hidden;
		closeReactionMenus(card);
		menu.hidden = !willOpen;
		trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
	}

	function doReaction(card, emojiId) {
		api('posts/' + card.dataset.postId + '/reaction', {
			method:  'POST',
			headers: { 'Content-Type': 'application/json' },
			body:    JSON.stringify({ emoji: emojiId }),
		})
			.then((data) => {
				updateReactionUI(card, data);
				closeReactionMenus();
			})
			.catch((error) => window.km_notification({ content: error.message, type: 'danger' }));
	}

	function showIdentityModal(card, emojiId) {
		let modal = document.getElementById('km-identity-modal');
		if (!modal) {
			modal = document.createElement('div');
			modal.id        = 'km-identity-modal';
			modal.className = 'modal fade';
			modal.setAttribute('tabindex', '-1');
			modal.setAttribute('aria-labelledby', 'km-identity-modal-label');
			modal.setAttribute('aria-hidden', 'true');
			modal.innerHTML = `
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title" id="km-identity-modal-label">${settings.i18n.identityTitle}</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body">
							<form id="km-identity-form" novalidate>
								<div class="mb-3">
									<label for="km-visitor-name" class="form-label">${settings.i18n.identityName}</label>
									<input type="text" class="form-control" id="km-visitor-name" required>
								</div>
								<div class="mb-3">
									<label for="km-visitor-email" class="form-label">${settings.i18n.identityEmail}</label>
									<input type="email" class="form-control" id="km-visitor-email" required>
								</div>
								<div class="mb-3">
									<label for="km-visitor-url" class="form-label">${settings.i18n.identityUrl}</label>
									<input type="url" class="form-control" id="km-visitor-url">
								</div>
							</form>
						</div>
						<div class="modal-footer justify-content-between">
							<button type="button" class="btn btn-outline-danger" id="km-identity-clear">${settings.i18n.identityClear}</button>
							<div>
								<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${settings.i18n.cancel}</button>
								<button type="button" class="btn btn-primary" id="km-identity-submit">${settings.i18n.identitySubmit}</button>
							</div>
						</div>
					</div>
				</div>`;
			document.body.appendChild(modal);

			modal.querySelector('#km-identity-submit').addEventListener('click', function () {
				const name  = modal.querySelector('#km-visitor-name').value.trim();
				const email = modal.querySelector('#km-visitor-email').value.trim();
				const url   = modal.querySelector('#km-visitor-url').value.trim();
				if (!name || !email) { window.km_notification({ content: settings.i18n.identityError, type: 'warning' }); return; }

				api('visitor-identity', {
					method:  'POST',
					headers: { 'Content-Type': 'application/json' },
					body:    JSON.stringify({ name, email, url }),
				}).then(() => {
					settings.hasIdentity  = true;
					settings.visitorName  = name;
					settings.visitorEmail = email;

					// Sync changes back to the global settings object
					window.keepMinutesAjax.hasIdentity  = true;
					window.keepMinutesAjax.visitorName  = name;
					window.keepMinutesAjax.visitorEmail = email;

					// Update the navbar button icon to Gravatar
					if (typeof updateIdentityButtonIcon === 'function') {
						updateIdentityButtonIcon();
					}

					bootstrap.Modal.getInstance(modal).hide();

					// If called from a reaction, continue with the reaction
					if (modal._kmReactionCard && modal._kmReactionEmoji) {
						doReaction(modal._kmReactionCard, modal._kmReactionEmoji);
					}
				}).catch((error) => window.km_notification({ content: error.message, type: 'danger' }));
			});

			// Clear identity handler
			modal.querySelector('#km-identity-clear').addEventListener('click', function () {
				removeVisitorIdentity(modal);
			});
		}

		modal._kmReactionCard  = card || null;
		modal._kmReactionEmoji = emojiId || null;

		// Pre-fill form fields from existing identity (if any)
		modal.querySelector('#km-visitor-name').value  = settings.visitorName || '';
		modal.querySelector('#km-visitor-email').value = settings.visitorEmail || '';
		modal.querySelector('#km-visitor-url').value   = '';

		new bootstrap.Modal(modal).show();
	}

	// Expose globally so the navbar button can call it
	window.kmShowIdentityModal = () => showIdentityModal();

	/**
	 * Remove visitor identity via API and update local state.
	 */
	function removeVisitorIdentity(modal) {
		api('remove-visitor-identity', {
			method:  'POST',
			headers: { 'Content-Type': 'application/json' },
		}).then(() => {
			settings.hasIdentity  = false;
			settings.visitorName  = '';
			settings.visitorEmail = '';

			// Sync changes back to the global settings object
			window.keepMinutesAjax.hasIdentity  = false;
			window.keepMinutesAjax.visitorName  = '';
			window.keepMinutesAjax.visitorEmail = '';

			// Clear form fields
			var form = modal.querySelector('#km-identity-form');
			if (form) form.reset();

			// Restore default icon in the navbar
			if (typeof updateIdentityButtonIcon === 'function') {
				updateIdentityButtonIcon();
			}

			window.km_notification({
				content: settings.i18n.identityCleared,
				type:    'success',
				delay: 3000,
			});
		}).catch((error) => window.km_notification({ content: error.message, type: 'danger' }));
	}

	async function openEditor(card) {
		const panel = card.querySelector('.km-card-panel');
		if (!panel) return;

		// Show loading state
		panel.innerHTML = `<div class="p-3 text-center text-muted"><i class="fa-solid fa-spinner fa-spin me-2"></i>${settings.i18n.editLoading}</div>`;
		panel.hidden = false;

		let postData;
		try {
			postData = await api('posts/' + card.dataset.postId);
		} catch (error) {
			panel.innerHTML = `<div class="p-3 text-danger">${error.message}</div>`;
			return;
		}

		// Build meta fields HTML
		const metaFields = (postData.meta || []);
		let metaHtml = '';
		if (metaFields.length > 0) {
			metaHtml = '<div class="km-edit-meta-section"><h5 class="km-edit-meta-title">' + settings.i18n.editMeta + '</h5>';
			metaFields.forEach(function (item) {
				metaHtml += `
				<div class="km-edit-meta-row">
					<label class="km-edit-meta-key">${item.key.replace(/"/g, '&quot;')}</label>
					<input class="km-field km-edit-meta-value" name="meta[${item.key.replace(/"/g, '&quot;')}]"
					       value="${(item.value || '').replace(/"/g, '&quot;')}">
				</div>`;
			});
			metaHtml += '</div>';
		}

		panel.innerHTML = `
			<form class="km-edit-form">
				<label class="km-edit-label">${settings.i18n.editTitle}</label>
				<input class="km-field" name="title"
				       value="${(postData.title || '').replace(/"/g, '&quot;')}"
				       placeholder="Title">
				<label class="km-edit-label">${settings.i18n.editContent}</label>
				<textarea class="km-field composer-textarea" name="content" rows="8" required>${(postData.content || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</textarea>
				${metaHtml}
				<div class="km-panel-actions">
					<button type="submit" class="btn-post">${settings.i18n.save}</button>
					<button type="button" class="btn-or" data-km-action="cancel-panel">${settings.i18n.cancel}</button>
				</div>
			</form>`;
		panel.querySelector('textarea').focus();
	}

	// ── 4.9 评论 ─────────────────────────────────────────────

	function commentIdFromReplyLink(link) {
		const m = (link.href || '').match(/replytocom=(\d+)/);
		return m ? m[1] : null;
	}

	function initCommentReplies(panel) {
		const form = panel.querySelector('.km-comment-form');
		if (!form) return;

		const parentInput     = form.querySelector('[name="comment_parent"]');
		const cancelBtn       = form.querySelector('.btn-cancel-reply');
		const textarea        = form.querySelector('textarea[name="comment"]');
		const defaultContainer = panel.querySelector('.km-comments-inner');

		if (cancelBtn) {
			cancelBtn.addEventListener('click', function () {
				if (parentInput) parentInput.value = '0';
				cancelBtn.hidden = true;
				textarea.setAttribute('placeholder', 'Write a comment...');
				textarea.value = '';
				if (defaultContainer && form.parentNode !== defaultContainer) {
					defaultContainer.appendChild(form);
				}
				panel.querySelector('.km-replying-to')?.remove();
			});
		}

		panel.querySelectorAll('.comment-reply-link').forEach(function (link) {
			link.addEventListener('click', function (e) {
				e.preventDefault();
				const commentId = commentIdFromReplyLink(link);
				if (!commentId) return;
				const li = link.closest('li');
				if (!li) return;

				let label = panel.querySelector('.km-replying-to');
				if (!label) {
					label = document.createElement('div');
					label.className = 'km-replying-to small text-muted mb-2';
				}
				const author = (li.querySelector('.fn') || {}).textContent || '';
				label.innerHTML = `<i class="fa-solid fa-reply"></i> ${settings.i18n.replyingTo} <strong>${author}</strong>`;

				if (li.nextSibling) {
					li.parentNode.insertBefore(label, li.nextSibling);
					li.parentNode.insertBefore(form, label.nextSibling);
				} else {
					li.parentNode.appendChild(label);
					li.parentNode.appendChild(form);
				}

				if (parentInput) parentInput.value = commentId;
				if (cancelBtn)   cancelBtn.hidden   = false;
				textarea.setAttribute('placeholder', `${settings.i18n.replyingTo} ${author}...`);
				textarea.focus();
			});
		});
	}

	function toggleComments(card) {
		const panel = card.querySelector('.km-card-panel');
		if (!panel) return;

		if (!panel.hidden && panel.classList.contains('is-comments')) {
			panel.hidden = true;
			panel.classList.remove('is-comments');
			return;
		}

		panel.hidden = false;
		panel.classList.add('is-comments');
		panel.innerHTML = '<div class="km-panel-loading"><i class="fa-solid fa-spinner fa-spin"></i></div>';

		api('posts/' + card.dataset.postId + '/comments?page=1')
			.then((data) => {
				panel.innerHTML = data.html;
				panel.dataset.commentsPage    = data.page    || 1;
				panel.dataset.commentsHasMore = data.has_more ? '1' : '0';
				initCommentReplies(panel);
				const count = card.querySelector('[data-km-action="comments"] .action-count');
				if (count) count.textContent = data.count;
			})
			.catch((error) => {
				panel.innerHTML = `<div class="alert alert-warning mb-0">${error.message}</div>`;
			});
	}

	// ── 4.10 Feed 事件委托 ───────────────────────────────────

	if (feed) {
		rememberCards();
		if (!document.body.classList.contains('single')) {
			const startPage = getStartPage();
			const feedType = feed.dataset.feedType || 'home';

			const isHome = feedType === 'home';
			const isGeolocation = feedType === 'geolocation';
			const isArchive = feedType === 'archive';

			if (isArchive) {
				const extraParams = {};
				['category', 'tag', 'author', 'year', 'monthnum', 'day', 'search', 'format'].forEach(key => {
					if (feed.dataset[key]) extraParams[key] = feed.dataset[key];
				});
				loadInitialFeed(extraParams).then((hasMore) => {
					initInfiniteScroll({
						feed,
						seenPosts,
						perPage: feedPageSize,
						initialHasMore: hasMore,
						skeletonHTML: skeletonCardHTML,
						getStartPage,
						endpoint: 'posts',
						extraParams,
						onBatchComplete: () => {
							if (window.initWaveSurfer) window.initWaveSurfer();
							initShareButtons();
							initExpandableImages();
							initExpandableAside();
							initFitty();
						},
					});
				});
			} else {
				let extraParams = {};
				let skeletonFn = skeletonCardHTML;
				if (isGeolocation) {
					extraParams = { geo_only: 'compact' };
					skeletonFn = skeletonGeolocationCompactCardHTML;
				} else if (!isHome) {
					extraParams = { image_only: '1' };
					skeletonFn = skeletonImageCardHTML;
				}

				loadInitialFeed(extraParams).then((hasMore) => {
					if (isHome && startPage === 1) startFeedPolling();
					initInfiniteScroll({
						feed,
						seenPosts,
						perPage: feedPageSize,
						initialHasMore: hasMore,
						skeletonHTML: skeletonFn,
						getStartPage,
						endpoint: 'posts',
						extraParams,
						onBatchComplete: () => {
							if (window.initWaveSurfer) window.initWaveSurfer();
							initShareButtons();
							initExpandableImages();
							initExpandableAside();
							initFitty();
						},
					});
				});

			}
		}

		feed.addEventListener('click', (event) => {
			// 加载更多评论
			const loadMoreBtn = event.target.closest('.km-load-more-comments');
			if (loadMoreBtn) {
				const panel = loadMoreBtn.closest('.km-card-panel');
				const card  = closestCard(loadMoreBtn);
				if (!panel || !card) return;

				const nextPage = parseInt(panel.dataset.commentsPage || 1, 10) + 1;
				loadMoreBtn.disabled  = true;
				loadMoreBtn.innerHTML = `<i class="fa-solid fa-spinner fa-spin me-1"></i>${settings.i18n.loadingOlder}`;

				api(`posts/${card.dataset.postId}/comments?page=${nextPage}&append=1`)
					.then((data) => {
						const ol = panel.querySelector('.commentlist');
						if (ol && data.html) ol.insertAdjacentHTML('beforeend', data.html);
						panel.dataset.commentsPage    = data.page    || nextPage;
						panel.dataset.commentsHasMore = data.has_more ? '1' : '0';
						initCommentReplies(panel);
						if (!data.has_more) {
							loadMoreBtn.remove();
						} else {
							loadMoreBtn.disabled  = false;
							loadMoreBtn.innerHTML = `<i class="fa-solid fa-chevron-down me-1"></i>${settings.i18n.loadOlder}`;
							loadMoreBtn.dataset.page = data.page;
						}
					})
					.catch(() => {
						loadMoreBtn.disabled  = false;
						loadMoreBtn.innerHTML = `<i class="fa-solid fa-chevron-down me-1"></i>${settings.i18n.loadOlder}`;
					});
				return;
			}

			const actionEl = event.target.closest('[data-km-action]');
			if (!actionEl) return;
			const card   = closestCard(actionEl);
			if (!card) return;
			const action = actionEl.dataset.kmAction;

			if (action === 'reaction-toggle') {
				toggleReactionMenu(card, actionEl);
				return;
			}
			if (action === 'reaction') {
				const emojiId = parseInt(actionEl.dataset.emoji, 10);
				if (!settings.isLoggedIn && !settings.hasIdentity) {
					showIdentityModal(card, emojiId);
				} else {
					doReaction(card, emojiId);
				}
				return;
			}
			if (action === 'comments') toggleComments(card);
			if (action === 'edit')     { event.preventDefault(); openEditor(card); }
			if (action === 'delete' && window.confirm(settings.i18n.deletePost)) {
				api('posts/' + card.dataset.postId, { method: 'DELETE' })
					.then(() => {
						seenPosts.delete(card.dataset.postId);
						card.classList.add('km-card-removing');
						window.setTimeout(() => card.remove(), 180);
					})
					.catch((error) => window.km_notification({ content: error.message, type: 'danger' }));
			}
			if (action === 'cancel-panel') {
				const panel = card.querySelector('.km-card-panel');
				if (panel) { panel.hidden = true; panel.innerHTML = ''; panel.classList.remove('is-comments'); }
			}
		});

		feed.addEventListener('submit', (event) => {
			const editForm    = event.target.closest('.km-edit-form');
			const commentForm = event.target.closest('.km-comment-form');
			const card        = closestCard(event.target);

			if (editForm && card) {
				event.preventDefault();
				const button = editForm.querySelector('.btn-post');
				setBusy(button, true, settings.i18n.saving);
				// Collect meta fields
				const metaInputs = editForm.querySelectorAll('[name^="meta["]');
				const meta = [];
				metaInputs.forEach(function (input) {
					const key = input.name.match(/^meta\[(.+)\]$/);
					if (key) {
						meta.push({ key: key[1], value: input.value });
					}
				});
				api('posts/' + card.dataset.postId, {
					method:  'POST',
					headers: { 'Content-Type': 'application/json' },
					body:    JSON.stringify({ title: editForm.elements.title.value, content: editForm.elements.content.value, meta: meta }),
				})
					.then((data)  => replaceCard(card, data.html, data.timestamp))
					.catch((error) => window.km_notification({ content: error.message, type: 'danger' }))
					.finally(() => setBusy(button, false));
			}

			if (commentForm && card) {
				event.preventDefault();
				const button = commentForm.querySelector('.btn-post');
				setBusy(button, true, settings.i18n.saving);
				const payload = {
					comment:        commentForm.elements.comment.value,
					comment_parent: commentForm.elements.comment_parent
						? parseInt(commentForm.elements.comment_parent.value, 10) || 0
						: 0,
				};
				if (!settings.isLoggedIn) {
					payload.author = commentForm.elements.author?.value ?? '';
					payload.email  = commentForm.elements.email?.value  ?? '';
				}
				api('posts/' + card.dataset.postId + '/comments', {
					method:  'POST',
					headers: { 'Content-Type': 'application/json' },
					body:    JSON.stringify(payload),
				})
					.then((data) => {
						const panel = card.querySelector('.km-card-panel');
						if (panel) {
							panel.innerHTML = data.html;
							panel.dataset.commentsPage    = data.page    || 1;
							panel.dataset.commentsHasMore = data.has_more ? '1' : '0';
							initCommentReplies(panel);
						}
						const count = card.querySelector('[data-km-action="comments"] .action-count');
						if (count) count.textContent = data.count;
					})
					.catch((error) => window.km_notification({ content: error.message, type: 'danger' }))
				.finally(() => setBusy(button, false));
			}
		});
	}

	document.addEventListener('click', (event) => {
		if (event.target.closest('.km-reaction-picker')) return;
		closeReactionMenus();
	});

	// ── 附件预览（extra 图片 / 音频）─────────────────────────

	function handleExtraImages(input) {
		const row = document.getElementById('extra-attachments');
		if (!row) return;
		row.hidden = false;

		const { accepted } = kmFilterOversizedFiles(input);

		accepted.forEach((file) => {
			_extraImageFiles.push(file);
			const thumb = document.createElement('div');
			thumb.className = 'attachment-thumb';
			const img = document.createElement('img');
			img.src = URL.createObjectURL(file);
			img.alt = file.name;
			const rm  = document.createElement('button');
			rm.className = 'att-remove';
			rm.type      = 'button';
			rm.innerHTML = '<i class="fa-solid fa-xmark"></i>';
			rm.onclick   = () => {
				_extraImageFiles = _extraImageFiles.filter(f => f !== file);
				URL.revokeObjectURL(img.src);
				thumb.remove();
				if (!row.children.length) row.hidden = true;
			};
			thumb.appendChild(img);
			thumb.appendChild(rm);
			row.appendChild(thumb);
		});

		row.hidden = !row.children.length;
		input.value = '';
	}

	function handleExtraAudio(input) {
		const row = document.getElementById('extra-attachments');
		if (!row) return;
		row.hidden = false;

		const { accepted } = kmFilterOversizedFiles(input);

		accepted.forEach((file) => {
			_extraAudioFiles.push(file);
			const thumb     = document.createElement('div');
			thumb.className = 'attachment-thumb audio-att';
			thumb.innerHTML = `<i class="fa-solid fa-music" style="font-size:1.2rem;color:var(--km-accent)"></i>
				<span>${file.name.substring(0, 10)}…</span>`;
			const rm  = document.createElement('button');
			rm.className = 'att-remove';
			rm.type      = 'button';
			rm.innerHTML = '<i class="fa-solid fa-xmark"></i>';
			rm.onclick   = () => {
				_extraAudioFiles = _extraAudioFiles.filter(f => f !== file);
				thumb.remove();
				if (!row.children.length) row.hidden = true;
			};
			thumb.appendChild(rm);
			row.appendChild(thumb);
		});

		row.hidden = !row.children.length;
		input.value = '';
	}
}

// ============================================================
// § 5  Composer UI（格式切换、RTE、标签、定位、附件）
// ============================================================

function initComposerUI() {
	if (!document.getElementById('km-compose-form')) return;

	// ── 5.1 登录状态 ──────────────────────────────────────────

	let isLoggedIn = false;

	function setLoginState(loggedIn) {
		isLoggedIn = loggedIn;
		const navGuest = document.getElementById('nav-guest');
		const navUser  = document.getElementById('nav-user');
		const composer = document.getElementById('composer');
		if (navGuest) navGuest.style.display    = loggedIn ? 'none' : '';
		if (navUser)  navUser.style.cssText      = loggedIn ? 'display:flex!important;align-items:center;gap:.5rem' : 'display:none!important';
		if (composer) composer.style.display     = loggedIn ? 'block' : 'none';
	}

	function toggleState() { setLoginState(!isLoggedIn); }

	function doLogin(e) {
		e.preventDefault();
		bootstrap.Modal.getInstance(document.getElementById('loginModal')).hide();
		setLoginState(true);
	}

	document.getElementById('logoutBtn')?.addEventListener('click', () => setLoginState(false));

	// ── 5.2 格式 Tab ─────────────────────────────────────────

	function switchFormat(btn) {
		document.querySelectorAll('.ftab').forEach((t) => t.classList.remove('active'));
		document.querySelectorAll('.pane').forEach((p) => p.classList.remove('active'));
		btn.classList.add('active');
		document.getElementById(btn.dataset.pane).classList.add('active');
		document.getElementById('post_format_field').value = btn.dataset.format;
		document.getElementById('extraImgBtn')?.classList.toggle('fading-out', btn.dataset.format === 'image');
		document.getElementById('extraAudioBtn')?.classList.toggle('fading-out', btn.dataset.format === 'audio');
		updateCharCount();
		if (btn.dataset.format === 'chat' && (!window._kmChatCharacters || !window._kmChatCharacters.length)) {
			openChatCharsModal();
		}
	}

	// ── 5.3 Tiptap 富文本编辑器（RTE） ───────────────────────

	const postContent = document.getElementById('post_content_field');
	const charCount   = document.getElementById('charCount');
	const rteEl       = document.getElementById('rte-content');
	const composerMaxChar = Math.max(
		1,
		parseInt(charCount?.dataset.maxChar || window.keepMinutesAjax?.maxChar || 280, 10) || 280
	);

	window.kmEditor = new Editor({
		element:    rteEl,
		extensions: [
			StarterKit,
			Placeholder.configure({
				placeholder: rteEl?.dataset.placeholder ?? 'Write something...',
			}),
		],
		content: '',
		onUpdate({ editor }) {
			if (postContent) postContent.value = editor.getHTML();
			updateCharCount();
		},
	});

	// 从 LocalStorage 恢复草稿
	const savedContent = localStorage.getItem('editorContent');
	if (savedContent) {
		try {
			window.kmEditor.commands.setContent(JSON.parse(savedContent));
		} catch (e) {
			localStorage.removeItem('editorContent');
		}
	}

	// 工具栏按钮
	const tiptapButtons = document.querySelectorAll('[data-tiptap-button]');
	tiptapButtons.forEach((button) => {
		button.addEventListener('click', (e) => {
			e.preventDefault();
			switch (button.dataset.tiptapButton) {
				case 'bold':        window.kmEditor.chain().focus().toggleBold().run();              break;
				case 'italic':      window.kmEditor.chain().focus().toggleItalic().run();            break;
				case 'strike':      window.kmEditor.chain().focus().toggleStrike().run();            break;
				case 'code':        window.kmEditor.chain().focus().toggleCode().run();              break;
				case 'h2':          window.kmEditor.chain().focus().toggleHeading({ level: 2 }).run(); break;
				case 'h3':          window.kmEditor.chain().focus().toggleHeading({ level: 3 }).run(); break;
				case 'bulletList':  window.kmEditor.chain().focus().toggleBulletList().run();        break;
				case 'orderedList': window.kmEditor.chain().focus().toggleOrderedList().run();       break;
				case 'blockquote':  window.kmEditor.chain().focus().toggleBlockquote().run();        break;
				case 'codeBlock':   window.kmEditor.chain().focus().toggleCodeBlock().run();         break;
			}
			updateActiveButtons();
		});
	});

	function updateActiveButtons() {
		const map = {
			bold:        () => window.kmEditor.isActive('bold'),
			italic:      () => window.kmEditor.isActive('italic'),
			strike:      () => window.kmEditor.isActive('strike'),
			code:        () => window.kmEditor.isActive('code'),
			h2:          () => window.kmEditor.isActive('heading', { level: 2 }),
			h3:          () => window.kmEditor.isActive('heading', { level: 3 }),
			bulletList:  () => window.kmEditor.isActive('bulletList'),
			orderedList: () => window.kmEditor.isActive('orderedList'),
			blockquote:  () => window.kmEditor.isActive('blockquote'),
			codeBlock:   () => window.kmEditor.isActive('codeBlock'),
		};
		tiptapButtons.forEach((button) => {
			const check = map[button.dataset.tiptapButton];
			button.classList.toggle('is-active', !!(check && check()));
		});
	}

	window.kmEditor.on('selectionUpdate', updateActiveButtons);
	window.kmEditor.on('update',          updateActiveButtons);

	function updateCharCount() {
		// 同步草稿到 LocalStorage
		if (window.kmEditor.getText().trim() === '') {
			localStorage.removeItem('editorContent');
		} else {
			localStorage.setItem('editorContent', JSON.stringify(window.kmEditor.getJSON()));
		}
		const activePane   = document.querySelector('.pane.active');
		const isWritePane  = activePane && activePane.id === 'pane-write';
		const len = isWritePane
			? window.kmEditor.getText().replace(/\n$/, '').length
			: (() => { const ta = activePane?.querySelector('textarea'); return ta ? ta.value.length : 0; })();
		if (charCount) {
			const remaining = composerMaxChar - len;
			charCount.textContent = String(remaining);
			charCount.style.color = remaining < 0
				? 'var(--km-red)'
				: (len >= composerMaxChar * 0.9 ? 'var(--km-yellow, #ffc107)' : 'var(--km-ink-muted)');
		}
	}

	window.keepMinutesUpdateCharCount = updateCharCount;

	document.querySelectorAll('.pane textarea, .pane input[type=text]')
		.forEach((el) => el.addEventListener('input', updateCharCount));


	//5.4
	// ── 5.5 标签选择器（Tag Picker） ─────────────────────────

	const PRESET_TAGS  = window.keepMinutesAjax.recentTags;
	let   selectedTags = [];

	function renderTagSuggestions(filter) {
		const box = document.getElementById('tagSuggestions');
		if (!box) return;
		box.innerHTML = '';
		PRESET_TAGS
			.filter((t) => !filter || t.includes(filter.toLowerCase()))
			.forEach((tag) => {
				const btn      = document.createElement('button');
				btn.type       = 'button';
				btn.className  = 'tag-suggestion' + (selectedTags.includes(tag) ? ' selected' : '');
				btn.textContent = tag;
				btn.onclick    = () => toggleTagFromPicker(tag);
				box.appendChild(btn);
			});
	}

	function syncTagDisplay() {
		const row    = document.getElementById('tag-display-row');
		const hidden = document.getElementById('tags_hidden');
		if (!row) return;
		row.innerHTML = '';
		row.hidden    = !selectedTags.length;
		selectedTags.forEach((tag) => {
			const pill      = document.createElement('div');
			pill.className  = 'tag-pill-active';
			// 修正：使用事件委托替代内联 onclick 字符串，避免全局函数依赖
			pill.innerHTML  = `<span>${tag}</span>
				<button type="button" aria-label="Remove tag">
					<i class="fa-solid fa-xmark"></i>
				</button>`;
			pill.querySelector('button').addEventListener('click', () => removeTag(tag));
			row.appendChild(pill);
		});
		if (hidden) hidden.value = selectedTags.map((t) => t.replace(/^#/, '')).join(',');
	}

	function addTag(tag) {
		if (!selectedTags.includes(tag)) selectedTags.push(tag);
		syncTagDisplay();
	}

	function removeTag(tag) {
		selectedTags = selectedTags.filter((t) => t !== tag);
		syncTagDisplay();
		renderTagSuggestions(document.getElementById('tagInput')?.value ?? '');
	}

	function toggleTagFromPicker(tag) {
		if (selectedTags.includes(tag)) {
			selectedTags = selectedTags.filter((t) => t !== tag);
		} else {
			addTag(tag);
		}
		renderTagSuggestions(document.getElementById('tagInput')?.value ?? '');
	}

	function toggleTagPicker() {
		const popup = document.getElementById('tagPickerPopup');
		if (!popup) return;
		const modal = new bootstrap.Modal(popup);
		modal.show();
		renderTagSuggestions('');
		setTimeout(() => document.getElementById('tagInput')?.focus(), 200);
	}

	function filterTags(val) { renderTagSuggestions(val); }

	function tagKeydown(e) {
		if (e.key !== 'Enter') return;
		e.preventDefault();
		const val = e.target.value.trim();
		if (!val) return;
		addTag(val.startsWith('#') ? val : '#' + val);
		e.target.value = '';
		renderTagSuggestions('');
		bootstrap.Modal.getInstance(document.getElementById('tagPickerPopup'))?.hide();
	}

	// ── 5.6 地理位置 ─────────────────────────────────────────

	function getLocation() {
		const btn  = document.getElementById('locationBtn');
		const disp = document.getElementById('location-display');
		const txt  = document.getElementById('location-text');
		if (!navigator.geolocation) {
			if (txt)  txt.textContent  = 'Geolocation not supported.';
			if (disp) disp.hidden      = false;
			return;
		}
		if (btn) { btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>'; btn.disabled = true; }

		navigator.geolocation.getCurrentPosition(
			(pos) => {
				showPosition(pos);
				const lat = pos.coords.latitude.toFixed(5);
				const lng = pos.coords.longitude.toFixed(5);
				document.getElementById('location_lat').value     = lat;
				document.getElementById('location_lng').value     = lng;
				document.getElementById('location_address').value = lat + ',' + lng;
				if (txt)  txt.textContent = lat + ', ' + lng;
				if (disp) disp.hidden     = false;
				if (btn)  { btn.innerHTML = '<i class="fa-solid fa-location-dot"></i>'; btn.classList.add('active'); btn.disabled = false; }
			},
			(err) => {
				window.km_notification({ content: 'Could not get location: ' + err.message, type: 'warning' });
				if (btn) { btn.innerHTML = '<i class="fa-solid fa-location-dot"></i>'; btn.disabled = false; }
			}
		);
	}

	// function clearLocation() {
	// 	document.getElementById('location_lat').value     = '';
	// 	document.getElementById('location_lng').value     = '';
	// 	document.getElementById('location_address').value = '';
	// 	document.getElementById('location-display').hidden = true;
	// 	document.getElementById('locationBtn')?.classList.remove('active');
	// }

	function handlePrimaryImages(input) {
		const row = document.getElementById('primary-img-preview');
		if (!row) return;

		const { accepted } = kmFilterOversizedFiles(input);

		accepted.forEach((file) => {
			window._primaryImageFiles.push(file);
			const thumb     = document.createElement('div');
			thumb.className = 'attachment-thumb';
			const img = document.createElement('img');
			img.src   = URL.createObjectURL(file);
			img.alt   = file.name;
			const rm  = document.createElement('button');
			rm.className = 'att-remove';
			rm.type      = 'button';
			rm.innerHTML = '<i class="fa-solid fa-xmark"></i>';
			rm.onclick   = () => {
				window._primaryImageFiles = window._primaryImageFiles.filter(f => f !== file);
				URL.revokeObjectURL(img.src);
				thumb.remove();
				if (!row.children.length) row.hidden = true;
			};
			thumb.appendChild(img);
			thumb.appendChild(rm);
			row.appendChild(thumb);
		});

		row.hidden = !row.children.length;
		input.value = '';
	}

	function handlePrimaryAudio(input) {
		const container = document.getElementById('primary-audio-preview');
		if (!container || !input.files[0]) return;

		const { accepted } = kmFilterOversizedFiles(input);
		const f = accepted[0];

		if (!f) {
			container.innerHTML = '';
			input.value = '';
			return;
		}

		container.innerHTML = `
			<div style="display:flex;align-items:center;gap:.6rem;background:var(--km-bg);
			            border-radius:var(--km-radius-sm);padding:.65rem .85rem;
			            border:1px solid var(--km-border)">
				<i class="fa-solid fa-file-waveform" style="color:var(--km-accent)"></i>
				<span style="font-size:.85rem;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${f.name}</span>
				<span style="font-size:.75rem;color:var(--km-ink-muted)">${(f.size / 1048576).toFixed(1)} MB</span>
			</div>`;
	}

	function handlePrimaryVideo(input) {
		const container = document.getElementById('primary-video-preview');
		if (!container || !input.files[0]) return;

		const { accepted } = kmFilterOversizedFiles(input);
		const f = accepted[0];

		if (!f) {
			container.innerHTML = '';
			input.value = '';
			return;
		}

		container.innerHTML = `
			<div style="display:flex;align-items:center;gap:.6rem;background:var(--km-bg);
			            border-radius:var(--km-radius-sm);padding:.65rem .85rem;
			            border:1px solid var(--km-border)">
				<i class="fa-solid fa-file-video" style="color:var(--km-accent)"></i>
				<span style="font-size:.85rem;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${f.name}</span>
				<span style="font-size:.75rem;color:var(--km-ink-muted)">${(f.size / 1048576).toFixed(1)} MB</span>
			</div>`;
	}

	// ── 5.8 音频录制 ────────────────────────────────────────────

	function showRecorderState(state) {
		['idle','active','done'].forEach(function(s) {
			const el = document.getElementById('recorder-' + s);
			if (el) el.style.display = s === state ? '' : 'none';
		});
	}

	function startAudioRecording() {
		const raw = window.keepMinutesAjax?.recordingMax;
		const maxSec = (raw && raw > 0) ? raw : 300;
		const maxLabel = document.getElementById('recorder-max-label');
		if (maxLabel) maxLabel.textContent = formatTime(maxSec) + ' max';
		const rec = window._kmRecorder;

		navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true } })
			.then(function(stream) {
				rec.stream = stream;
				var mimeType = 'audio/webm;codecs=opus';
				var options = { audioBitsPerSecond: 96000 };
				if (MediaRecorder.isTypeSupported(mimeType)) options.mimeType = mimeType;
				rec.recorder = new MediaRecorder(stream, options);
				rec.chunks = [];
				rec.recorder.ondataavailable = function(e) { if (e.data.size > 0) rec.chunks.push(e.data); };
				rec.recorder.onstop = function() {
					clearInterval(rec.timer);
					if (rec.chunks.length) {
						var blob = new Blob(rec.chunks, { type: 'audio/webm' });
						rec.blob = new File([blob], 'recording-' + Date.now() + '.webm', { type: 'audio/webm' });
						var preview = document.getElementById('recorder-preview');
						if (preview) preview.src = URL.createObjectURL(blob);
						var info = document.getElementById('recorder-file-info');
						if (info) info.textContent = rec.blob.name + ' (' + (rec.blob.size / 1024).toFixed(1) + ' KB)';
						showRecorderState('done');
					}
					if (rec.stream) { rec.stream.getTracks().forEach(function(t) { t.stop(); }); rec.stream = null; }
				};
				rec.recorder.start();
				rec.startTime = Date.now();
				showRecorderState('active');
				rec.timer = setInterval(function() {
					var elapsed = Math.floor((Date.now() - rec.startTime) / 1000);
					var timerEl = document.getElementById('recorder-timer');
					if (timerEl) timerEl.textContent = formatTime(elapsed);
					if (elapsed >= maxSec) stopAudioRecording();
				}, 200);
			})
			.catch(function(err) {
				km_notification({ content: window.keepMinutesAjax?.i18n?.recordingLimit + ': ' + err.message, type: 'danger', delay: 5000 });
			});
	}

	function stopAudioRecording() {
		var r = window._kmRecorder;
		if (r.recorder && r.recorder.state === 'recording') {
			r.recorder.stop();
		}
	}

	function reRecordAudio() {
		var rec = window._kmRecorder;
		rec.blob = null;
		var preview = document.getElementById('recorder-preview');
		if (preview) { if (preview.src) URL.revokeObjectURL(preview.src); preview.src = ''; }
		var info = document.getElementById('recorder-file-info');
		if (info) info.textContent = '';
		showRecorderState('idle');
	}

	// ── 5.9 挂载到 window（供 HTML 内联属性调用） ─────────────

	Object.assign(window, {
		setLoginState,
		toggleState,
		doLogin,
		switchFormat,
		toggleTagPicker,
		filterTags,
		tagKeydown,
		removeTag,
		getLocation,
		handlePrimaryImages,
		handlePrimaryAudio,
		handlePrimaryVideo,
		startAudioRecording,
		stopAudioRecording,
		reRecordAudio,
		showRecorderState,
		openChatCharsModal,
		addChatCharacter,
		removeChatCharacter,
		addChatLine,
	});

	// ── 5.9 Chat Composer ──────────────────────────────────────

	function updateChatUI() {
		const radioGroup = document.getElementById('chat-char-radio-group');
		const lineInput  = document.getElementById('chat-line-input');
		const addBtn     = document.getElementById('chat-add-line-btn');
		const hidden     = document.getElementById('chat-characters-hidden');
		const hasChars   = window._kmChatCharacters.length > 0;
		const settings   = window.keepMinutesAjax;

		const currentChecked = radioGroup?.querySelector('input[name="chat-character-radio"]:checked')?.value;
		const checkedChar = currentChecked && window._kmChatCharacters.includes(currentChecked)
			? currentChecked
			: (window._kmChatCharacters[0] || '');

		if (radioGroup) {
			radioGroup.innerHTML = window._kmChatCharacters.map(function (c, i) {
				var id = 'chat-char-radio-' + i;
				var checked = c === checkedChar ? ' checked' : '';
				return '<input type="radio" class="btn-check" name="chat-character-radio" id="' + id + '" value="' + escAttr(c) + '" autocomplete="off"' + checked + '>' +
					'<label class="btn btn-outline-primary" for="' + id + '">' +
						escHtml(c) +
						'<button type="button" class="btn-close ms-3" onclick="event.stopPropagation();removeChatCharacter(\'' + escAttr(c) + '\')" aria-label="' + settings.i18n.remove + '"></button>' +
					'</label>';
			}).join('');
		}
		if (lineInput) lineInput.disabled = !hasChars;
		if (addBtn)    addBtn.disabled    = !hasChars;
		if (hidden)    hidden.value       = window._kmChatCharacters.join(',');
	}

	function openChatCharsModal() {
		updateChatModalPills();
		var modalEl = document.getElementById('chatCharModal');
		if (!modalEl) return;
		var existingModal = bootstrap.Modal.getInstance(modalEl);
		if (existingModal) {
			existingModal.show();
		} else {
			new bootstrap.Modal(modalEl).show();
		}
		document.getElementById('chat-char-input')?.focus();
	}

	function updateChatModalPills() {
		const settings = window.keepMinutesAjax;
		var pills = document.getElementById('chat-char-modal-pills');
		if (!pills) return;
		pills.innerHTML = window._kmChatCharacters.map(function (c) {
			return '<span class="chat-character-badge">' +
				escHtml(c) +
				'<button type="button" class="btn-close btn-close-sm ms-1" onclick="removeChatCharacter(\'' + escAttr(c) + '\')" aria-label="' + settings.i18n.remove + '"></button>' +
				'</span>';
		}).join('');
	}

	window.onChatCharKeydown = function (event) {
		if (event.key === 'Enter') {
			event.preventDefault();
			addChatCharacter();
		}
	};

	function addChatCharacter() {
		var input = document.getElementById('chat-char-input');
		if (!input) return;
		var name = input.value.trim();
		if (!name) return;
		if (window._kmChatCharacters.indexOf(name) !== -1) {
			input.value = '';
			input.focus();
			return;
		}
		window._kmChatCharacters.push(name);
		input.value = '';
		input.focus();
		updateChatUI();
		updateChatModalPills();
	}

	function removeChatCharacter(name) {
		window._kmChatCharacters = window._kmChatCharacters.filter(function (c) { return c !== name; });
		updateChatUI();
		updateChatModalPills();
	}

	function addChatLine() {
		var input    = document.getElementById('chat-line-input');
		var textarea = document.getElementById('chat-content');
		if (!input || !textarea) return;
		var selected = document.querySelector('input[name="chat-character-radio"]:checked');
		var character = selected ? selected.value : '';
		var line      = input.value.trim();
		if (!character || !line) return;

		var safeChar = character.replace(/"/g, '&quot;').replace(/\[/g, '&#91;').replace(/\]/g, '&#93;');
		var safeLine = line.replace(/"/g, '&quot;').replace(/\[/g, '&#91;').replace(/\]/g, '&#93;');
		var shortcode = '[km_chat c="' + safeChar + '" l="' + safeLine + '"]';

		if (textarea.value) textarea.value += '\n';
		textarea.value += shortcode;
		input.value = '';
		autoResizeTextarea(textarea);
		textarea.scrollTop = textarea.scrollHeight;
		input.focus();
		updateCharCount();
	}

	function escHtml(str) {
		return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	function escAttr(str) {
		return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;');
	}
}


// ============================================================
// § 5.10 Composer 拖拽上传
// ============================================================

function initUploadZoneDragDrop() {
	document.querySelectorAll('.upload-zone[data-input-id]').forEach((zone) => {
		if (zone.dataset.dragInited) return;
		zone.dataset.dragInited = '1';

		const inputId = zone.dataset.inputId;
		const input   = document.getElementById(inputId);
		if (!input) return;

		['dragenter', 'dragover', 'dragleave', 'drop'].forEach((evt) => {
			zone.addEventListener(evt, (e) => {
				e.preventDefault();
				e.stopPropagation();
			});
		});

		zone.addEventListener('dragenter', () => {
			zone.classList.add('drag-over');
		});

		zone.addEventListener('dragover', () => {
			zone.classList.add('drag-over');
		});

		zone.addEventListener('dragleave', () => {
			zone.classList.remove('drag-over');
		});

		zone.addEventListener('drop', (e) => {
			zone.classList.remove('drag-over');
			const files = e.dataTransfer.files;
			if (!files.length) return;

			const dt = new DataTransfer();
			for (const f of files) {
				dt.items.add(f);
			}
			input.files = dt.files;
			input.dispatchEvent(new Event('change', { bubbles: true }));
		});
	});
}


// ============================================================
// § 6  分享功能 (Share)
// ============================================================

function initShareButtons() {
	document.querySelectorAll('.action-btn.share').forEach((btn) => {
		if (btn.dataset.shareInited) return;
		btn.dataset.shareInited = '1';

		btn.addEventListener('click', (e) => {
			e.preventDefault();

			const url = btn.href || window.location.href;
			const shareData = {
				title: document.title,
				url: url,
			};

			if (navigator.share) {
				navigator.share(shareData).catch(() => {});
			} else {
				const settings = window.keepMinutesAjax || {};
				navigator.clipboard.writeText(url).then(() => {
					window.km_notification({
						content: (settings.i18n && settings.i18n.linkCopied) ? settings.i18n.linkCopied : 'Link copied',
						type: 'success',
						delay: 3000,
					});
				}).catch(() => {});
			}
		});
	});
}


// ============================================================
// § 7  可展开图片 (Expandable Image)
// ============================================================

function initExpandableImages() {
	document.querySelectorAll('body:not(.page-template-default,.single) figure.wp-block-image:not(.wp-block-gallery figure.wp-block-image)').forEach(async (figure) => {
		if (figure.querySelector('.km-image-wrap')) return;

		const children = [];
		const caption = figure.querySelector('figcaption');
		while (figure.firstChild && figure.firstChild !== caption) {
			children.push(figure.removeChild(figure.firstChild));
		}
		if (children.length === 0) return;

		const wrap = document.createElement('div');
		wrap.className = 'km-image-wrap';
		children.forEach((child) => wrap.appendChild(child));
		figure.insertBefore(wrap, caption || null);

		const btn = document.createElement('button');
		btn.className = 'km-image-toggle';
		btn.type = 'button';
		btn.setAttribute('aria-label', 'Expand image');
		btn.innerHTML = '<i class="fa-solid fa-chevron-down"></i>';
		figure.appendChild(btn);

		const imgs = wrap.querySelectorAll('img');
		if (imgs.length) {
			await Promise.all(Array.from(imgs).map((img) =>
				img.complete
					? Promise.resolve()
					: new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })
			));
		}

		if (wrap.scrollHeight > wrap.offsetHeight) {
			figure.classList.add('km-image-overflow');
		}

	function toggleImage() {
		const expanded = wrap.classList.toggle('is-expanded');
		btn.setAttribute('aria-label', expanded ? 'Collapse image' : 'Expand image');
	}

	btn.addEventListener('click', toggleImage);

	imgs.forEach((img) => {
		img.addEventListener('click', (e) => {
			e.preventDefault();
			toggleImage();
		});
	});
});
}


// ============================================================
// § 7.1  Notification 标题自适应字号 (Fitty)
// ============================================================

/**
 * 让 notification 卡片（无图时）的标题与副标题字号自适应容器宽度。
 * 通过 data-fitty-init 保证幂等，重复调用不会重复实例化。
 */
function initFitty() {
	document.querySelectorAll('.km-notification-title').forEach((el) => {
		if (el.dataset.fittyInit) return;
		el.dataset.fittyInit = '1';
		fitty(el, { minSize: 16, maxSize: 42, multiLine: true });
	});

	document.querySelectorAll('.km-notification-subtitle').forEach((el) => {
		if (el.dataset.fittyInit) return;
		el.dataset.fittyInit = '1';
		fitty(el, { minSize: 12, maxSize: 24, multiLine: true });
	});
}

window.initFitty = initFitty;


// ============================================================
// § 7.2  可展开的 Aside 正文 (Expandable Aside)
// ============================================================

/**
 * Aside 卡片的正文默认完全隐藏，点击“展开”按钮后显示全文。
 * 通过 data-km-aside-init 保证幂等。
 */
function initExpandableAside() {
	const settings = window.keepMinutesAjax || {};

	document.querySelectorAll('.km-aside-content-wrap').forEach((wrap) => {
		if (wrap.dataset.kmAsideInit) return;
		wrap.dataset.kmAsideInit = '1';

		const body = wrap.querySelector('.km-aside-body');
		const toggle = wrap.querySelector('.km-aside-toggle');
		if (!body || !toggle) return;

		toggle.addEventListener('click', () => {
			const expanded = body.classList.toggle('is-expanded');
			toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
			toggle.setAttribute('aria-label', expanded
				? (settings.i18n && settings.i18n.asideCollapse ? settings.i18n.asideCollapse : 'Collapse')
				: (settings.i18n && settings.i18n.asideExpand ? settings.i18n.asideExpand : 'Expand'));
		});
	});
}

window.initExpandableAside = initExpandableAside;


// ============================================================
// § 7.3  卡片下拉菜单溢出修复 (Card Dropdown Overflow)
// ============================================================

/**
 * .km-card 设置了 overflow:hidden，卡片高度较小时（如无正文的 aside），
 * 下拉菜单会被卡片裁剪导致编辑/删除按钮不可见、不可点击。
 * 打开下拉时临时释放卡片的 overflow 并提升层级，关闭后还原。
 * 使用文档级事件委托，无限加载/轮询插入的新卡片同样生效。
 */
function initCardDropdowns() {
	if (document.body.dataset.kmDropdownInit) return;
	document.body.dataset.kmDropdownInit = '1';

	document.addEventListener('show.bs.dropdown', (event) => {
		const toggle = event.target;
		const card = toggle.closest('.article-warper[data-post-id]');
		if (!card) return;
		card.classList.add('is-dropdown-open');
	});

	document.addEventListener('hide.bs.dropdown', (event) => {
		const toggle = event.target;
		const card = toggle.closest('.article-warper[data-post-id]');
		if (!card) return;
		card.classList.remove('is-dropdown-open');
	});
}

window.initCardDropdowns = initCardDropdowns;


// ============================================================
// § 8  Gallery Lightbox (Bootstrap Modal + Carousel)
// ============================================================

let kmGalleryModal = null;
let kmGalleryCarousel = null;

function initGalleryLightbox() {
	const modalEl = document.getElementById('km-gallery-lightbox');
	if (modalEl) {
		kmGalleryModal = new bootstrap.Modal(modalEl, { keyboard: true });
		kmGalleryCarousel = new bootstrap.Carousel(modalEl.querySelector('.carousel'), { interval: false, touch: true });
	} else {
		const html = `
		<div class="modal fade km-gallery-lightbox" id="km-gallery-lightbox" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-xl">
				<div class="modal-content">
					<div class="modal-body p-0 position-relative">
						<button type="button" class="btn-close btn-close-white km-lightbox-close position-absolute top-0 end-0 m-3" data-bs-dismiss="modal" aria-label="Close"></button>
						<div id="km-gallery-carousel" class="carousel slide" data-bs-ride="false" data-bs-interval="false" data-bs-touch="true">
							<div class="carousel-inner"></div>
							<button class="carousel-control-prev" type="button" data-bs-target="#km-gallery-carousel" data-bs-slide="prev">
								<span class="carousel-control-prev-icon" aria-hidden="true"></span>
								<span class="visually-hidden">Previous</span>
							</button>
							<button class="carousel-control-next" type="button" data-bs-target="#km-gallery-carousel" data-bs-slide="next">
								<span class="carousel-control-next-icon" aria-hidden="true"></span>
								<span class="visually-hidden">Next</span>
							</button>
							<div class="carousel-indicators"></div>
						</div>
					</div>
				</div>
			</div>
		</div>`;
		const template = document.createElement('template');
		template.innerHTML = html.trim();
		document.body.appendChild(template.content.firstChild);
		const newModalEl = document.getElementById('km-gallery-lightbox');
		kmGalleryModal = new bootstrap.Modal(newModalEl, { keyboard: true });
		kmGalleryCarousel = new bootstrap.Carousel(newModalEl.querySelector('.carousel'), { interval: false, touch: true });
	}

	const modalContent = document.querySelector('#km-gallery-lightbox .modal-content');
	modalContent.addEventListener('click', function (e) {
		if (!e.target.closest('.carousel, .carousel-control-prev, .carousel-control-next, .km-lightbox-close, .carousel-indicators')) {
			kmGalleryModal.hide();
		}
	});

	document.addEventListener('click', function (e) {
		const trigger = e.target.closest('.km-gallery-trigger');
		if (!trigger) return;
		e.preventDefault();

		const galleryId = trigger.getAttribute('data-gallery-id');
		const index = parseInt(trigger.getAttribute('data-index'), 10);
		const siblings = document.querySelectorAll('.km-gallery-trigger[data-gallery-id="' + galleryId + '"]');

		const carouselInner = document.querySelector('#km-gallery-lightbox .carousel-inner');
		const indicators = document.querySelector('#km-gallery-lightbox .carousel-indicators');
		carouselInner.innerHTML = '';
		indicators.innerHTML = '';

		siblings.forEach(function (el, i) {
			const img = el.querySelector('img');
			const src = el.getAttribute('href');
			const alt = img ? img.getAttribute('alt') || '' : '';
			const activeClass = i === index ? ' active' : '';
			const slide = document.createElement('div');
			slide.className = 'carousel-item' + activeClass;
			slide.innerHTML = '<img src="' + src + '" alt="' + alt.replace(/"/g, '&quot;') + '" class="d-block img-fluid mx-auto">';
			carouselInner.appendChild(slide);

			const indicator = document.createElement('button');
			indicator.type = 'button';
			indicator.setAttribute('data-bs-target', '#km-gallery-carousel');
			indicator.setAttribute('data-bs-slide-to', i);
			if (i === index) indicator.className = 'active';
			indicator.setAttribute('aria-current', i === index ? 'true' : 'false');
			indicator.setAttribute('aria-label', 'Slide ' + (i + 1));
			indicators.appendChild(indicator);
		});

		kmGalleryCarousel = new bootstrap.Carousel(document.querySelector('#km-gallery-lightbox .carousel'), { interval: false, touch: true });
		kmGalleryModal.show();
	});
}

// ============================================================
// § 9  媒体 URL 粘贴转短代码 (Media Embed Paste)
// ============================================================

/**
 * 监听 composer 中 video_url / audio_url 输入框的 paste 事件，
 * 将已知外部媒体 URL 自动转换为 WordPress 短代码。
 * 支持的平台：YouTube、Bilibili（含 b23.tv 短链）、Vimeo、Spotify、网易云音乐。
 */
function initMediaEmbedPaste() {
	const videoInput = document.querySelector('input[name="video_url"]');
	const audioInput = document.querySelector('input[name="audio_url"]');

	function resolveB23ShortLink(url, input) {
		const s = window.keepMinutesAjax || {};
		fetch(s.root + 'keep-minutes/v1/resolve-b23', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': s.nonce
			},
			body: JSON.stringify({ url: url })
		})
			.then(function (res) { return res.json(); })
			.then(function (data) {
				if (data.bvid) {
					input.value = '[bilibili id="' + data.bvid + '"]';
				}
			})
			.catch(function (err) { console.error('b23.tv resolve failed:', err); });
	}

	function handlePaste(e) {
		var input = e.currentTarget;
		setTimeout(function () {
			var url = input.value.trim();
			if (!url) return;

			// ── YouTube ──────────────────────────────────
			if (~url.indexOf('youtube.com/watch')) {
				var vid = url.split('?')[1].split('=')[1].split('&')[0];
				input.value = '[youtube id=' + vid + ']';
				return;
			}
			if (~url.indexOf('youtu.be/')) {
				var vid = url.split('.be/')[1].split('?')[0];
				input.value = '[youtube id=' + vid + ']';
				return;
			}

			// ── Bilibili ─────────────────────────────────
			if (~url.indexOf('bilibili.com/video/') || ~url.indexOf('m.bilibili.com/video/')) {
				var bvid = url.split('?')[0].split('video/')[1];
				bvid = bvid.replace(/\/$/, '');
				input.value = '[bilibili id="' + bvid + '"]';
				return;
			}
			if (~url.indexOf('b23.tv/')) {
				resolveB23ShortLink(url, input);
				return;
			}

			// ── Vimeo ────────────────────────────────────
			if (~url.indexOf('vimeo.com/')) {
				var vid = url.split('/').pop().split('?')[0];
				if ((/^(\+|-)?\d+$/.test(vid)) && parseInt(vid, 10) > 0) {
					input.value = '[vimeo id=' + vid + ']';
				}
				return;
			}

			// ── Spotify ──────────────────────────────────
			if (~url.indexOf('open.spotify.com/')) {
				if (~url.indexOf('track/')) {
					input.value = '[spotify type="track" id=' + url.split('track/')[1].split('?')[0] + ']';
				} else if (~url.indexOf('playlist/')) {
					input.value = '[spotify type="playlist" id=' + url.split('playlist/')[1].split('?')[0] + ']';
				} else if (~url.indexOf('album/')) {
					input.value = '[spotify type="album" id=' + url.split('album/')[1].split('?')[0] + ']';
				}
				return;
			}

			// ── 网易云音乐 ───────────────────────────────
			if (~url.indexOf('music.163.com')) {
				if (~url.indexOf('song?id=')) {
					input.value = '[nemusic type="song" id=' + url.split('?')[1].split('=')[1] + ']';
				} else if (~url.indexOf('playlist?id=')) {
					input.value = '[nemusic type="playlist" id=' + url.split('?')[1].split('=')[1] + ']';
				}
				return;
			}
		}, 0);
	}

	if (videoInput) {
		videoInput.addEventListener('paste', handlePaste);
	}
	if (audioInput) {
		audioInput.addEventListener('paste', handlePaste);
	}
}

// ============================================================
// § 10 DOMContentLoaded — 统一入口
// ============================================================

/**
 * 当有访客身份时，将导航栏按钮图标替换为 name 的首字符（类似已登录用户的头像样式）。
 * 清除身份后也需要调用来恢复默认图标。
 * 放在模块顶层作用域，以便 initFeed 内的 removeVisitorIdentity 也能调用。
 */
function updateIdentityButtonIcon() {
	var btn = document.getElementById('km-identity-nav-btn');
	if (!btn) return;

	var hasIdentity = window.keepMinutesAjax && window.keepMinutesAjax.hasIdentity;
	var name        = window.keepMinutesAjax ? window.keepMinutesAjax.visitorName : '';

	if (hasIdentity && name) {
		// 取首字符：中英文通用，通过解构字符串取第一个码点
		var initial = (typeof Array.from !== 'undefined')
			? Array.from(name)[0]
			: name.charAt(0);
		initial = initial.toUpperCase();
		btn.innerHTML = '<div class="user-avatar-sm">' + initial + '</div>';
		btn.title = name;
	} else {
		// 恢复默认图标
		btn.innerHTML = '<i class="fa-solid fa-user-pen"></i>';
		btn.title = 'Set identity';
	}
}

// ── 6.11 所有 textarea 自动高度适配输入内容 ─────────────

function autoResizeTextarea(el) {
	el.style.height = 'auto';
	var scrollH = el.scrollHeight;
	var minH    = getTextareaMinHeight(el);
	el.style.height       = Math.max(scrollH, minH) + 'px';
	el.style.overflowY    = 'hidden';
}

function getTextareaMinHeight(el) {
	var rows = parseInt(el.getAttribute('rows'), 10);
	if (!rows || rows < 1) rows = 2;
	var cs = getComputedStyle(el);
	var pt = parseFloat(cs.paddingTop)    || 0;
	var pb = parseFloat(cs.paddingBottom) || 0;
	var bt = parseFloat(cs.borderTopWidth)    || 0;
	var bb = parseFloat(cs.borderBottomWidth) || 0;
	var lh = parseFloat(cs.lineHeight);
	if (isNaN(lh)) {
		var fs = parseFloat(cs.fontSize) || 16;
		lh = fs * 1.3;
	}
	return lh * rows + pt + pb + bt + bb;
}

// ============================================================
// § 11 Aside Icon Selector
// ============================================================

window.selectAside = function () {
	const modalEl = document.getElementById('asideModal');
	if (!modalEl) return;
	const modal = new bootstrap.Modal(modalEl);
	modal.show();
};

window.pickAsideIcon = function (iconName, btnEl) {
	const hidden = document.getElementById('aside');
	const btn   = document.getElementById('aside-select');
	if (hidden) { hidden.value = iconName; hidden.name = 'cstatus'; }
	if (btn) {
		const iEl = btn.querySelector('i');
		if (iEl) iEl.className = 'icon-font ico-' + iconName + ' fs-3';
	}
	document.querySelectorAll('#asideModal .aside-icon-btn').forEach(function (b) { b.classList.remove('selected'); });
	if (btnEl) btnEl.classList.add('selected');
	bootstrap.Modal.getInstance(document.getElementById('asideModal'))?.hide();
};

function initAutoResizeTextareas() {
	document.addEventListener('input', function (e) {
		if (e.target.tagName === 'TEXTAREA') {
			autoResizeTextarea(e.target);
		}
	});
	document.querySelectorAll('textarea').forEach(autoResizeTextarea);
}


// ============================================================
// § 12 Poll Functionality
// ============================================================

const MAX_POLL_OPTIONS = 7;
const MIN_POLL_OPTIONS = 2;

function createPollOptionInput(index, value = '') {
	const wrapper = document.createElement('div');
	wrapper.className = 'poll-option-row d-flex gap-2 mb-2 align-items-center';
	wrapper.dataset.index = index;

	const input = document.createElement('input');
	input.type = 'text';
	input.className = 'km-field flex-grow-1 poll-option-input';
	input.placeholder = window.keepMinutesAjax?.i18n?.poll?.optionPlaceholder?.replace('%d', index);
	input.value = value;
	input.maxLength = 200;

	const removeBtn = document.createElement('button');
	removeBtn.type = 'button';
	removeBtn.className = 'btn btn-outline-danger btn poll-remove-option border-0';
	removeBtn.innerHTML = '<i class="fa-solid fa-xmark"></i>';
	removeBtn.onclick = () => removePollOption(removeBtn);
	removeBtn.setAttribute('aria-label', window.keepMinutesAjax?.i18n?.poll?.removeOption);

	wrapper.appendChild(input);
	wrapper.appendChild(removeBtn);

	return wrapper;
}

window.openPollModal = function () {
	const modalEl = document.getElementById('pollModal');
	if (!modalEl) return;

	const container = document.getElementById('poll-options-container');
	if (container) {
		// Reset to default 2 options
		container.innerHTML = '';
		for (let i = 1; i <= MIN_POLL_OPTIONS; i++) {
			container.appendChild(createPollOptionInput(i));
		}
		updatePollOptionButtons();
	}

	const modal = new bootstrap.Modal(modalEl);
	modal.show();
};

window.addPollOption = function () {
	const container = document.getElementById('poll-options-container');
	if (!container) return;

	const currentCount = container.querySelectorAll('.poll-option-row').length;
	if (currentCount >= MAX_POLL_OPTIONS) {
		window.km_notification({
			content: window.keepMinutesAjax?.i18n?.poll?.maxOptionsReached?.replace('%d', MAX_POLL_OPTIONS),
			type: 'warning'
		});
		return;
	}

	const newIndex = currentCount + 1;
	container.appendChild(createPollOptionInput(newIndex));
	updatePollOptionButtons();
};

window.removePollOption = function (btn) {
	const container = document.getElementById('poll-options-container');
	if (!container) return;

	const rows = container.querySelectorAll('.poll-option-row');
	if (rows.length <= MIN_POLL_OPTIONS) {
		window.km_notification({
			content: window.keepMinutesAjax?.i18n?.poll?.minOptions?.replace('%d', MIN_POLL_OPTIONS),
			type: 'warning'
		});
		return;
	}

	const row = btn.closest('.poll-option-row');
	if (row) row.remove();

	// Re-index remaining options
	container.querySelectorAll('.poll-option-row').forEach((r, idx) => {
		r.dataset.index = idx + 1;
		const input = r.querySelector('.poll-option-input');
		if (input) {
			input.placeholder = window.keepMinutesAjax?.i18n?.poll?.optionPlaceholder?.replace('%d', idx + 1);
		}
	});

	updatePollOptionButtons();
};

function updatePollOptionButtons() {
	const container = document.getElementById('poll-options-container');
	const addBtn = document.getElementById('poll-add-option');
	if (!container || !addBtn) return;

	const count = container.querySelectorAll('.poll-option-row').length;

	// Show/hide remove buttons based on min options
	container.querySelectorAll('.poll-remove-option').forEach(btn => {
		btn.style.display = count <= MIN_POLL_OPTIONS ? 'none' : 'inline-flex';
	});

	// Disable add button if max reached
	addBtn.disabled = count >= MAX_POLL_OPTIONS;

	// Update hint
	const hint = document.getElementById('poll-option-hint');
	if (hint) {
		hint.textContent = window.keepMinutesAjax?.i18n?.poll?.maxOptionsHint?.replace('%d', MAX_POLL_OPTIONS - count);
	}
}

window.insertPollShortcode = function () {
	const container = document.getElementById('poll-options-container');
	if (!container) return;

	const inputs = container.querySelectorAll('.poll-option-input');
	const options = [];

	inputs.forEach(input => {
		const val = input.value.trim();
		if (val) options.push(val);
	});

	if (options.length < MIN_POLL_OPTIONS) {
		window.km_notification({
			content: window.keepMinutesAjax?.i18n?.poll?.minOptions?.replace('%d', MIN_POLL_OPTIONS),
			type: 'warning'
		});
		return;
	}

	// Build shortcode
	let shortcode = '[poll]\n';
	options.forEach((opt, idx) => {
		shortcode += `[op ${idx + 1}]${opt}[/op]\n`;
	});
	shortcode += '[/poll]';

	// Insert into Tiptap editor
	if (window.kmEditor) {
		window.kmEditor.chain().focus().insertContent(shortcode).run();
	}

	// Close modal
	const modalEl = document.getElementById('pollModal');
	if (modalEl) {
		bootstrap.Modal.getInstance(modalEl)?.hide();
	}

	window.km_notification({
		content: window.keepMinutesAjax?.i18n?.poll?.inserted,
		type: 'success'
	});
};

// Progress Bar (kmpgs)
window.openProgressModal = function () {
	const modalEl = document.getElementById('progressModal');
	if (!modalEl) return;

	const range = document.getElementById('progress-value-range');
	const value = document.getElementById('progress-value');
	if (range && value) {
		range.value = 50;
		value.value = 50;
	}

	const modal = new bootstrap.Modal(modalEl);
	modal.show();
};

// Keep range slider and number input in sync
document.addEventListener('input', (e) => {
	if (e.target.id === 'progress-value-range') {
		const num = document.getElementById('progress-value');
		if (num) num.value = e.target.value;
	} else if (e.target.id === 'progress-value') {
		const range = document.getElementById('progress-value-range');
		if (range) range.value = Math.max(0, Math.min(100, Number(e.target.value) || 0));
	}
});

window.insertProgressShortcode = function () {
	const valueEl = document.getElementById('progress-value');
	const typeEl = document.getElementById('progress-type');
	if (!valueEl || !typeEl) return;

	let value = parseInt(valueEl.value, 10);
	if (isNaN(value)) value = 0;
	value = Math.max(0, Math.min(100, value));

	const type = ['success', 'info', 'warning', 'danger'].includes(typeEl.value) ? typeEl.value : 'success';

	const shortcode = `[kmpgs v="${value}" type="${type}"]`;

	if (window.kmEditor) {
		window.kmEditor.chain().focus().insertContent(shortcode).run();
	}

	const modalEl = document.getElementById('progressModal');
	if (modalEl) {
		bootstrap.Modal.getInstance(modalEl)?.hide();
	}

	window.km_notification({
		content: window.keepMinutesAjax?.i18n?.progress?.inserted,
		type: 'success'
	});
};

// Poll Voting
function handlePollVote(optionEl, postId, optionIndex) {
	// Check if already voted (visual check)
	const pollList = optionEl.closest('.km-poll');
	if (pollList?.classList.contains('has-voted')) {
		window.km_notification({
			content: window.keepMinutesAjax?.i18n?.poll?.alreadyVoted,
			type: 'warning'
		});
		return;
	}

	// Check cookie
	if (document.cookie.includes('poll_' + postId + '=1')) {
		window.km_notification({
			content: window.keepMinutesAjax?.i18n?.poll?.alreadyVoted,
			type: 'warning'
		});
		return;
	}

	const nonce = pollList?.dataset?.nonce;
	if (!nonce) return;

	// Show loading state
	optionEl.classList.add('voting');
	const progressBar = optionEl.querySelector('.km-poll-bar');
	const countEl = optionEl.querySelector('.km-poll-option-count');

	api(`posts/${postId}/poll`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ option: optionIndex, nonce: nonce })
	})
		.then(data => {
			if (!data.success) throw new Error(data.message || 'Vote failed');

			// Update UI with animation
			const results = data.results || [];
			const total = data.total || 0;

			// Update all option bars with animation
			const pollOptions = pollList?.querySelectorAll('.km-poll-option');
			pollOptions?.forEach(opt => {
				const optIndex = parseInt(opt.dataset.option, 10);
				const result = results.find(r => r.option === optIndex);
				if (result) {
					const bar = opt.querySelector('.km-poll-bar');
					const count = opt.querySelector('.km-poll-option-count');
					if (bar) {
						// Animate width
						bar.style.transition = 'width 0.5s ease-out';
						bar.style.width = result.percent + '%';
						bar.setAttribute('aria-valuenow', result.percent);
						bar.dataset.targetWidth = result.percent + '%';
					}
					if (count) {
						count.textContent = result.count;
						count.style.display = 'inline'; // Show count after voting
					}
				}
			});
			
			// Mark poll as voted so future renders know to show counts
			if (pollList) {
				pollList.dataset.hasVoted = '1';
			}

			// Update total sum
			if (pollList) {
				pollList.dataset.sum = total;
			}

			// Mark as voted
			pollList?.classList.add('has-voted');
			pollOptions?.forEach(opt => opt.classList.add('voted'));

			// Set cookie (7 days)
			const expires = new Date();
			expires.setTime(expires.getTime() + 7 * 24 * 60 * 60 * 1000);
			document.cookie = `poll_${postId}=1; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;

window.km_notification({
				content: window.keepMinutesAjax?.i18n?.poll?.voteSuccess,
				type: 'success'
			});
		})
		.catch(err => {
			window.km_notification({
				content: err.message || window.keepMinutesAjax?.i18n?.error,
				type: 'danger'
			});
		})
		.finally(() => {
			optionEl.classList.remove('voting');
		});
}

function initPollVoting() {
	// Event delegation for poll options
	document.addEventListener('click', (e) => {
		const optionEl = e.target.closest('.km-poll-option[data-option]');
		if (!optionEl) return;

		// Only handle clicks on interactive polls (not static)
		const pollList = optionEl.closest('.km-poll:not(.km-poll-static)');
		if (!pollList) return;

		const postId = pollList.dataset.postId;
		const optionIndex = parseInt(optionEl.dataset.option, 10);

		if (postId && optionIndex > 0) {
			handlePollVote(optionEl, postId, optionIndex);
		}
	});
}

window.initPollVoting = initPollVoting;

// Repost functionality
window.handleRepostClick = function (card, postId) {
	// Check if user can edit (author or editor)
	const settings = window.keepMinutesAjax;
	if (!settings?.isLoggedIn) {
		window.km_notification({
			content: window.keepMinutesAjax?.i18n?.repost?.loginRequired,
			type: 'warning'
		});
		return;
	}

	// Insert repost shortcode into editor
	const shortcode = `[repost id="${postId}"]`;

	if (window.kmEditor) {
		window.kmEditor.chain().focus().insertContent(shortcode).run();
	}

	// Switch to write pane if not already there
	const writeTab = document.querySelector('.ftab[data-format="status"]');
	if (writeTab && !writeTab.classList.contains('active')) {
		writeTab.click();
	}

	window.km_notification({
		content: window.keepMinutesAjax?.i18n?.repost?.inserted,
		type: 'success'
	});
}

function initRepostButtons() {
	// Event delegation for repost buttons
	document.addEventListener('click', (e) => {
		const repostBtn = e.target.closest('.action-btn.repost[data-id]');
		if (!repostBtn) return;

		e.preventDefault();

		const card = repostBtn.closest('.article-warper[data-post-id]');
		const postId = repostBtn.dataset.id;

		if (card && postId) {
			handleRepostClick(card, postId);
		}
	});
}

window.initRepostButtons = initRepostButtons;

document.addEventListener('DOMContentLoaded', () => {
	// 显示主题版本
	if (window.keepMinutesAjax?.themeVersion) {
		console.info(
			"%c KEEP MINUTES 3 %c " + window.keepMinutesAjax.themeVersion,
			"color: orange; font-weight: bold; background: black",
			"color: darkblue; font-weight: bold; background: white"
		);
	}

	// 服务端传递的通知
	if (typeof keepMinutesAjax !== 'undefined' && Array.isArray(keepMinutesAjax.notices)) {
		keepMinutesAjax.notices.forEach((notice) => window.km_notification(notice));
	}

	// § 2  WaveSurfer 音频播放器
	initWaveSurfer();

	// § 3  主题切换
	initThemeToggle();

	// § 4  Ajax Feed（依赖 keepMinutesAjax.root）
	initFeed();

	// § 5  Composer UI（仅在存在 #km-compose-form 时初始化）
	initComposerUI();

	// § 5.10 Composer 拖拽上传
	initUploadZoneDragDrop();

	// § 6  分享按钮
	initShareButtons();

	// § 8  Gallery Lightbox
	initGalleryLightbox();

	// § 7  可展开图片
	initExpandableImages();

	// § 7.2  可展开的 Aside 正文
	initExpandableAside();

	// § 7.3  卡片下拉菜单溢出修复
	initCardDropdowns();

	// § 7.1  Notification 标题自适应字号
	initFitty();

	// § 9  媒体 URL 粘贴转短代码
	initMediaEmbedPaste();

	// 所有 textarea 自动高度适配输入内容
	initAutoResizeTextareas();

	// § 12 Poll 投票
	initPollVoting();

	// Repost 转发按钮
	initRepostButtons();

	// 导航栏身份按钮 — 手动唤起身份 Modal
	const identityBtn = document.getElementById('km-identity-nav-btn');
	if (identityBtn) {
		identityBtn.addEventListener('click', function () {
			if (window.kmShowIdentityModal) {
				window.kmShowIdentityModal();
			}
		});

		// 如果已有缓存的访客身份，按钮图标替换为 Gravatar 头像
		updateIdentityButtonIcon();
	}
});
